// RallyPopup.tsx
import React, { ReactNode, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  PaperProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import SmartToyIcon from "@mui/icons-material/SmartToy";

import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
} from "@mui/material";

import Collapse from "@mui/material/Collapse";

import { ShotsHistogramFilter } from "./ShotHistogramFilter";
import { ShotNumberOverlay } from "./RallyBoardPopup";
import { FirefightClip } from "./GameStory_firefights";
import {
  FocusedReasonStakeForTeam,
  shotMatchesFocusedFilter,
  STORY_FILTER_ICON,
  teamRgba,
  RenderPlayerIdentity,
  PlayerIndexMap,
  toDisplayPlayerId,
  //renderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";

/**
 * Minimal ChatGPT/OpenAI-style knot icon (monochrome).
 * Renders with currentColor so it matches MUI IconButton styling.
 */
export default function ChatGptIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      {/* Simple knot-ish mark (clean, small, works at 18–20px) */}
      <path
        fill="currentColor"
        d="M12 2.5c2.1 0 4.1 1.1 5.3 2.9 1.8.2 3.4 1.4 4.1 3.1.7 1.7.4 3.7-.8 5.1.3 1.8-.5 3.7-2 4.8-1.5 1.1-3.5 1.3-5.2.4-1.7.9-3.7.7-5.2-.4-1.5-1.1-2.3-3-2-4.8-1.2-1.4-1.5-3.4-.8-5.1.7-1.7 2.3-2.9 4.1-3.1C7.9 3.6 9.9 2.5 12 2.5Zm-1.9 4.2-1.7 1c-.7.4-1.2 1.1-1.2 1.9v2c0 .8.4 1.5 1.1 1.9l1.7 1-.2.3c-.4.7-.4 1.6 0 2.3l1 1.7c.4.7 1.1 1.1 1.9 1.1h2c.8 0 1.5-.4 1.9-1.1l1-1.7c.4-.7.4-1.6 0-2.3l-.2-.3 1.7-1c.7-.4 1.1-1.1 1.1-1.9v-2c0-.8-.5-1.5-1.2-1.9l-1.7-1 .2-.3c.4-.7.4-1.6 0-2.3l-1-1.7c-.4-.7-1.1-1.1-1.9-1.1h-2c-.8 0-1.5.4-1.9 1.1l-1 1.7c-.4.7-.4 1.6 0 2.3l.2.3Zm1.6 1.2.3-.6c.1-.2.3-.3.5-.3h.9c.2 0 .4.1.5.3l.5.8.9.5c.2.1.3.3.3.5v.9c0 .2-.1.4-.3.5l-.9.5-.5.8c-.1.2-.3.3-.5.3h-.9c-.2 0-.4-.1-.5-.3l-.5-.8-.9-.5c-.2-.1-.3-.3-.3-.5v-.9c0-.2.1-.4.3-.5l.9-.5Z"
      />
    </SvgIcon>
  );
}

// start influence report types etc

type Team01 = 0 | 1;

type InfluenceRallyRef = {
  rallyIndex: number;
  winnerTeam: Team01 | null;
  servingTeam: Team01 | null;
  teamWonRally: boolean | null;
};

type InfluencePick = {
  team: Team01;
  context: "SERVE" | "RECEIVE";
  impact: "HELPED" | "HURT";
  scenario: string;
  mode: string;

  featureKey: string;
  featureLabel: string;
  layman: string;

  delta: number;
  importance: number;
  supportNote?: string;

  ralliesToReview: InfluenceRallyRef[];
};

type TeamVideoTriage = {
  team: Team01;
  serve: {
    hurtLost: InfluencePick[];
    hurtSurvived: InfluencePick[];
    helpedWon: InfluencePick[];
    helpedLost: InfluencePick[];
    specials?: { missedServe?: InfluencePick; shortReturn?: InfluencePick };
  };
  receive: {
    hurtLost: InfluencePick[];
    hurtSurvived: InfluencePick[];
    helpedWon: InfluencePick[];
    helpedLost: InfluencePick[];
    specials?: { missedReturn?: InfluencePick; shortReturn?: InfluencePick };
  };
};

// type SideoutGameInfluenceReport = {
//   videoTriageByTeam: {
//     team0: TeamVideoTriage;
//     team1: TeamVideoTriage;
//   };
// };

export type SideoutInfluenceRow = {
  featureKey: string;
  featureLabel: string;

  // Layman content for UI (always present)
  featureLayman: string;
  computedHow: string;
  caveats: string[];

  // rates & delta for this mode’s outcome
  rateTrue: number;
  rateFalse: number;
  delta: number;

  nTrue: number;
  nFalse: number;

  trueRallyIndices: number[];
  trueGoodOutcomeRallyIndices: number[];
  trueBadOutcomeRallyIndices: number[];

  falseRallyIndices: number[];
  falseGoodOutcomeRallyIndices: number[];
  falseBadOutcomeRallyIndices: number[];

  unknownRallyIndices: number[];
};

export type SideoutTeamGameStats = {
  team: Team01;

  // Points scored while this team served
  pointsScored: number;
  pointsByRallyIndex: number[];

  // Serve stats
  serveRallies: number;
  servePoints: number;
  servePointRate: number;

  // Receive stats
  receiveRallies: number;

  // Points conceded while receiving (opponent scored on their serve)
  pointsConcededOnReceive: number;
  pointsConcededOnReceiveRallyIndices: number[];
  opponentPointRateOnTheirServe: number;

  // Sideouts earned while receiving (you won rally while receiving)
  receiveSideouts: number;
  receiveSideoutsRallyIndices: number[];
  receiveSideoutRate: number;

  influence: {
    onServePoints: SideoutInfluenceRow[];
    onReceiveSideout: SideoutInfluenceRow[];
    onReceiveLostGamePoint: SideoutInfluenceRow[];
  };
};

export type SideoutMode =
  | "onServePoints"
  | "onReceiveSideout"
  | "onReceiveLostGamePoint";

export type SideoutGameInfluenceExplanation = {
  overview: string[];

  definitions: {
    modes: Record<SideoutMode, string>;
    delta: string;
    rates: string;
    support: string;
    importance: string;
  };

  featureCatalog: Array<{
    key: string;
    label: string;
    layman: string;
    computedHow: string;
    caveats: string[];
  }>;

  howToUse: {
    guidedTriage: string[];
    whatToWatchForExamples: string[];
  };
};

export type RallyRef = {
  rallyIndex: number;
  winnerTeam: Team01 | null;
  servingTeam: Team01 | null;
  teamWonRally: boolean | null;
};

export type ShotEventSource = "insights" | "cvBall" | "derived";

export type ShotEvent = {
  // identity
  rallyIndex: number;
  shotIndex: number; // 0=serve,1=return,2=third,...

  // classification
  kind: string; // "popup_caused", "popup_exploited", "poach", "speedup", "reset", "putaway", "fault_net", ...
  source: ShotEventSource;

  // attribution (optional, but you want it)
  team?: Team01 | null;
  playerSlot?: number | null; // PBV “player_id” slot (0..3) if known
  playerId?: number | null; // if you have stable ids elsewhere

  // timing (optional)
  tMsAbs?: number | null; // absolute ms in match video timeline
  tMsFromRallyStart?: number | null;

  // location (optional)
  ball?: { x?: number | null; y?: number | null; z?: number | null } | null;

  // freeform payload
  details?: any;
};

export type RallyFeatureEvidence = {
  shotEvents: ShotEvent[];
  featureShotIndex: Record<string, number[]>;
  featureCatalog?: string[]; // optional
};

export type SideoutRegressionEvidenceClip = {
  rallyIndex: number;
  shotIndex: number;
  shotIndexRange?: [number, number] | null;
  clipStartMsAbs: number;
  clipEndMsAbs: number;
};

export type SideoutRegressionRallyRow = {
  rallyIndex: number;
  pointKey: string;

  servingTeam: Team01 | null;
  winnerTeam: Team01 | null;

  segmentStartMs: number;
  segmentEndMs: number;

  // Targets
  yTeam0Point: 0 | 1;
  yTeam1Point: 0 | 1;
  yDiff: -1 | 0 | 1;

  yTeam0ServeMissed: 0 | 1;
  yTeam1ServeMissed: 0 | 1;

  // Features (booleans or null)
  featuresTeam0: Record<string, boolean | null>;
  featuresTeam1: Record<string, boolean | null>;

  // Evidence & rally payload (optional)
  evidence: any | null;
  pbvRally: any | null;

  yTeam0Sideout?: 0 | 1;
  yTeam1Sideout?: 0 | 1;

  /** signed: +1 if team0 sideout, -1 if team1 sideout, else 0 */
  yTeam0SideoutDiff?: -1 | 0 | 1;

  /** signed: +1 if team1 sideout, -1 if team0 sideout, else 0 */
  yTeam1SideoutDiff?: -1 | 0 | 1;
};

export type RegressionCoefficientRow = {
  featureKey: string; // e.g. "t0__weMissedReturn"
  coef: number;

  // convenience
  absCoef: number;
  direction: "positive" | "negative" | "zero";

  // clip evidence samples
  evidenceClips: SideoutRegressionEvidenceClip[];
};

export type SideoutSingleGameRegressionModelResult = {
  target: "team0Points" | "team1Points" | "diffPoints" | "serverWon";

  // Ridge regression diagnostics
  nSamples: number;
  nFeatures: number;
  lambda: number;

  // Aggregations that map to final score interpretation
  // - "actual" is an integer count
  // - "predicted" is rounded to an integer count for easy comparison
  actual: number;
  predicted: number;

  // optional: raw sum of per-rally predictions before rounding
  predictedRaw?: number;

  // Ranked coefficient views
  topAll: RegressionCoefficientRow[];
  topUpstreamView: RegressionCoefficientRow[];

  // Raw coefficients
  coefficients: Record<string, number>;

  // Optional: blacklist/debug info
  blacklist?: {
    enabled: boolean;
    droppedBaseKeys: string[];
    keptBaseKeys: string[];
    // new:
    rarity: {
      minTrueAnyTeam: number;
      droppedRareBaseKeys: string[];
    };
  };
};

export type SideoutSingleGameRegressionResult = {
  expectedPoints: { team0: number; team1: number };
  expectedDiff: number;

  actualPoints: { team0: number; team1: number };
  actualDiff: number;

  models: {
    team0Points: SideoutSingleGameRegressionModelResult;
    team1Points: SideoutSingleGameRegressionModelResult;
    diffPoints: SideoutSingleGameRegressionModelResult;
    serverWon: SideoutSingleGameRegressionModelResult;
  };
};

export type SideoutGameInfluenceReport = {
  // Allow missing (as you requested)
  gameOutcome?: { team0: number; team1: number } | undefined;
  inferredWinnerTeam: Team01 | null;

  kitchenArrivalRows: KitchenArrivalRow[];

  computedPoints: { team0: number; team1: number };
  netPointProxy: { team0: number; team1: number };

  team0: SideoutTeamGameStats;
  team1: SideoutTeamGameStats;

  // Big human-readable summary (ordered, used in UI)
  explanation: SideoutGameInfluenceExplanation;

  // For UI triage popup
  videoTriageByTeam: {
    team0: TeamVideoTriage;
    team1: TeamVideoTriage;
  };

  // Optional quick header lines per team, already includes rally indices
  sideoutSummaryByTeam: {
    team0: string[];
    team1: string[];
  };
  rallyEvidenceByRallyIndex?: Record<number, RallyFeatureEvidence>;

  // ✅ NEW: regression output for this single game (computed on the fly)
  singleGameRegression?: SideoutSingleGameRegressionResult;

  // ✅ NEW (debug-friendly): what regression used per rally
  regressionRallyRows?: SideoutRegressionRallyRow[];
};

// end influence report types etc

function isAllSelected<T extends string>(set: Set<T>, all: readonly T[]) {
  return all.every((v) => set.has(v));
}

function chipsForSet<T extends string>(opts: {
  label: string;
  set: Set<T>;
  all: readonly T[];
  renderValue?: (v: T) => string;
  onDeleteValue: (v: T) => void;
}) {
  const { label, set, all, renderValue, onDeleteValue } = opts;
  if (set.size === 0) return [];
  if (isAllSelected(set, all)) return []; // treat "all" as default -> hide
  return Array.from(set).map((v) => ({
    key: `${label}:${v}`,
    text: `${label}: ${renderValue ? renderValue(v) : String(v)}`,
    onDelete: () => onDeleteValue(v),
  }));
}

export function SelectionSummarySticky(props: {
  activePick: InfluenceNavItem | null;
  onClearPick: () => void;

  filters: Filters;
  setFilters: (updater: (prev: Filters) => Filters) => void;

  shotRange: [number, number] | null; // null = no filter
  fullShotRange?: [number, number] | null; // ✅ add
  onClearShotRange: () => void;

  onResetAll: () => void;

  // if you have a DialogTitle bar etc
  stickyTopPx?: number; // default 0
}) {
  const stickyTopPx = props.stickyTopPx ?? 0;

  const filterChips = useMemo(() => {
    const out: { key: string; text: string; onDelete: () => void }[] = [];

    out.push(
      ...chipsForSet({
        label: "Got to kitchen",
        set: props.filters.gotToKitchen,
        all: ["yes", "no", "unknown"] as const,
        renderValue: (v) =>
          v === "yes" ? "Yes" : v === "no" ? "No" : "Unknown",
        onDeleteValue: (v) =>
          props.setFilters((f) => {
            const next = new Set(f.gotToKitchen);
            next.delete(v);
            return { ...f, gotToKitchen: next };
          }),
      }),
    );

    out.push(
      ...chipsForSet({
        label: "Kitchen opp",
        set: props.filters.hadOpportunity,
        all: ["yes", "no", "unknown"] as const,
        renderValue: (v) =>
          v === "yes" ? "Yes" : v === "no" ? "No" : "Unknown",
        onDeleteValue: (v) =>
          props.setFilters((f) => {
            const next = new Set(f.hadOpportunity);
            next.delete(v);
            return { ...f, hadOpportunity: next };
          }),
      }),
    );

    out.push(
      ...chipsForSet({
        label: "Winner",
        set: props.filters.winnerSides,
        all: ["near", "far", "unknown"] as const,
        renderValue: (v) =>
          v === "near" ? "Near" : v === "far" ? "Far" : "Unknown",
        onDeleteValue: (v) =>
          props.setFilters((f) => {
            const next = new Set(f.winnerSides);
            next.delete(v);
            return { ...f, winnerSides: next };
          }),
      }),
    );

    out.push(
      ...chipsForSet({
        label: "Server",
        set: props.filters.servingSides,
        all: ["near", "far", "unknown"] as const,
        renderValue: (v) =>
          v === "near" ? "Near" : v === "far" ? "Far" : "Unknown",
        onDeleteValue: (v) =>
          props.setFilters((f) => {
            const next = new Set(f.servingSides);
            next.delete(v);
            return { ...f, servingSides: next };
          }),
      }),
    );

    out.push(
      ...chipsForSet({
        label: "AI",
        set: props.filters.hasAi,
        all: ["has_ai", "no_ai"] as const,
        renderValue: (v) => (v === "has_ai" ? "Has" : "None"),
        onDeleteValue: (v) =>
          props.setFilters((f) => {
            const next = new Set(f.hasAi);
            next.delete(v);
            return { ...f, hasAi: next };
          }),
      }),
    );

    return out;
  }, [props.filters, props.setFilters]);

  const shotChip = useMemo(() => {
    if (!props.shotRange) return null;

    // ✅ treat full range as default -> hide chip
    if (
      props.fullShotRange &&
      props.shotRange[0] === props.fullShotRange[0] &&
      props.shotRange[1] === props.fullShotRange[1]
    ) {
      return null;
    }

    const [a, b] = props.shotRange;
    const text = a === b ? `Number Shots: ${a}` : `Number Shots: ${a}–${b}`;
    return { text, onDelete: props.onClearShotRange };
  }, [props.shotRange, props.onClearShotRange, props.fullShotRange]);

  const hasAnything =
    !!props.activePick || filterChips.length > 0 || !!shotChip;

  if (!hasAnything) return null;

  return (
    <Box
      sx={{
        position: "sticky",
        top: stickyTopPx,
        zIndex: 20,
        bgcolor: "#fff",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        py: 0.75,
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          //px: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700, mr: 0.5 }}>
          Active Factor:
        </Typography>

        {props.activePick ? (
          <Chip
            size="small"
            label={`${props.activePick.context}: ${
              props.activePick.featureLabel
            }${
              typeof props.activePick.id === "number"
                ? ` (${props.activePick.id})`
                : ""
            }`}
            onDelete={props.onClearPick}
            variant="outlined"
          />
        ) : null}

        {filterChips.map((c) => (
          <Chip
            key={c.key}
            size="small"
            label={c.text}
            onDelete={c.onDelete}
            variant="outlined"
          />
        ))}

        {shotChip ? (
          <Chip
            size="small"
            label={shotChip.text}
            onDelete={shotChip.onDelete}
            variant="outlined"
          />
        ) : null}

        <Box sx={{ flex: "1 1 auto" }} />

        <Button
          size="small"
          onClick={props.onResetAll}
          sx={{ fontSize: 12, py: 0.25, minHeight: 26 }}
        >
          Clear all
        </Button>
      </Box>
    </Box>
  );
}

function soloInSet<T>(all: readonly T[], v: T): Set<T> {
  // solo => only v is selected
  // (If you later want right-click-again to clear, we can add a toggle here.)
  return new Set<T>([v]);
}

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function overlayOpacityMsRel(t: number, o: ShotNumberOverlay): number {
  if (t < o.showFromMsRel || t > o.hideAtMsRel) return 0;

  if (t < o.fullFromMsRel) {
    const d = o.fullFromMsRel - o.showFromMsRel;
    if (d <= 1) return 1;
    return clamp01((t - o.showFromMsRel) / d);
  }

  if (t < o.fadeStartMsRel) return 1;

  const d = o.hideAtMsRel - o.fadeStartMsRel;
  if (d <= 1) return 0;
  return clamp01(1 - (t - o.fadeStartMsRel) / d);
}

function pickActiveShotOverlay(
  overlays: ShotNumberOverlay[] | undefined,
  tMsRel: number,
): ShotNumberOverlay | null {
  if (!overlays || overlays.length === 0) return null;

  // If multiple overlap, prefer the latest shotNumber.
  for (let i = overlays.length - 1; i >= 0; i--) {
    const o = overlays[i]!;
    if (tMsRel >= o.showFromMsRel && tMsRel <= o.hideAtMsRel) return o;
  }
  return null;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

const Group = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 0.15,
      p: 0.75,
      borderRadius: 2,
      border: "1px solid rgba(0,0,0,0.12)",
      bgcolor: "rgba(0,0,0,0.02)",
    }}
  >
    <Box sx={{ fontSize: 11.5, fontWeight: 900, opacity: 0.7, lineHeight: 1 }}>
      {label}
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {children}
    </Box>
  </Box>
);

type Tri = "yes" | "no" | "unknown";
type SegType = "full" | "partial" | "other";
type HasAi = "has_ai" | "no_ai";

type Filters = {
  winnerSides: Set<Side>; // near/far/unknown
  servingSides: Set<Side>; // near/far/unknown
  gotToKitchen: Set<Tri>; // yes/no/unknown
  hadOpportunity: Set<Tri>; // yes/no/unknown
  likelyIncorrectScore: Set<Tri>; // yes/no/unknown
  hasAi: Set<HasAi>; // has_ai/no_ai
  segmentType: Set<SegType>; // full/partial/other
};

const DEFAULT_FILTERS: Filters = {
  winnerSides: new Set<Side>(["near", "far", "unknown"]),
  servingSides: new Set<Side>(["near", "far", "unknown"]),
  gotToKitchen: new Set<Tri>(["yes", "no", "unknown"]),
  likelyIncorrectScore: new Set<Tri>(["yes", "no", "unknown"]),
  hadOpportunity: new Set<Tri>(["yes", "no", "unknown"]),
  hasAi: new Set<HasAi>(["has_ai", "no_ai"]),
  segmentType: new Set<SegType>(["full", "partial", "other"]),
};

function fmtSide(s: Side): string {
  return s === "near" ? "Near" : s === "far" ? "Far" : "Unknown";
}

function fmtTriBool(v: boolean | null | undefined): "Yes" | "No" | "Unknown" {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "Unknown";
}

function matchesLikelyCorrectScore(
  r: KitchenArrivalRow,
  filter: "all" | "true" | "false" | "unknown",
) {
  const v = r.likely_correct_score;
  if (filter === "all") return true;
  if (filter === "unknown") return v == null;
  if (filter === "true") return v === true;
  return v === false;
}

function buildRallySummaryChips(r: KitchenArrivalRow): string[] {
  const parts: string[] = [];

  parts.push(`Shots: ${typeof r.numShots === "number" ? r.numShots : "?"}`);

  // Winner
  parts.push(`Winner: ${fmtSide(r.winnerSide)}`);

  parts.push(
    `Possible incorrect score: ${fmtTriBool(!r.likely_correct_score)}`,
  );

  // Serve side (if known)
  if (r.servingSide && r.servingSide !== "unknown") {
    parts.push(`Server: ${fmtSide(r.servingSide)}`);
  } else {
    parts.push(`Server: Unknown`);
  }

  if (typeof r.serveSpeedMPH === "number") {
    parts.push(`Serve speed (MPH): ${r.serveSpeedMPH.toFixed(0)}`);
  } else {
    parts.push(`Serve speed (MPH): Unknown`);
  }

  // “Got to kitchen” (serving team arrival success)
  // Your field name implies this is about the *serving team*.
  parts.push(`Server got to Kitchen: ${fmtTriBool(r.servingArrivalSuccess)}`);

  // Opportunity
  parts.push(
    `Server Kitchen Opportunity: ${fmtTriBool(r.servingHadOpportunity)}`,
  );

  // Arrival time (only if success + time known)
  if (
    r.servingArrivalSuccess === true &&
    typeof r.servingEarliestArrivalMsFromStart === "number"
  ) {
    parts.push(
      `Arrive: ${(r.servingEarliestArrivalMsFromStart / 1000).toFixed(2)}s`,
    );
  }

  // Segment type (full vs partial)
  const segType = r.segmentReason?.toLowerCase().includes("full")
    ? "Full rally"
    : r.segmentReason?.toLowerCase().includes("partial")
      ? "Partial"
      : "Other";
  parts.push(`Seg: ${segType}`);

  return parts;
}

function summarizeFilters(filters: Filters): string {
  const parts: string[] = [];

  const pick = <T,>(
    label: string,
    all: T[],
    active: Set<T>,
    fmt: (x: T) => string,
  ) => {
    if (active.size === all.length) return; // means "no filtering"
    const vals = all.filter((x) => active.has(x)).map(fmt);
    if (vals.length === 0) return;
    parts.push(`${label}: ${vals.join("/")}`);
  };

  pick<Side>("Winner", ["near", "far", "unknown"], filters.winnerSides, (s) =>
    s === "near" ? "Near" : s === "far" ? "Far" : "Unknown",
  );

  pick<Side>("Serve", ["near", "far", "unknown"], filters.servingSides, (s) =>
    s === "near" ? "Near" : s === "far" ? "Far" : "Unknown",
  );

  pick<Tri>("Kitchen", ["yes", "no", "unknown"], filters.gotToKitchen, (t) =>
    t === "yes" ? "Success" : t === "no" ? "Failed" : "Unknown",
  );

  pick<Tri>("Opp", ["yes", "no", "unknown"], filters.hadOpportunity, (t) =>
    t === "yes" ? "Yes" : t === "no" ? "No" : "Unknown",
  );

  pick<Tri>(
    "Opp",
    ["yes", "no", "unknown"],
    filters.likelyIncorrectScore,
    (t) => (t === "yes" ? "Yes" : t === "no" ? "No" : "Unknown"),
  );

  pick<HasAi>("AI", ["has_ai", "no_ai"], filters.hasAi, (x) =>
    x === "has_ai" ? "Has" : "None",
  );

  pick<SegType>(
    "Seg",
    ["full", "partial", "other"],
    filters.segmentType,
    (x) => (x === "full" ? "Full" : x === "partial" ? "Partial" : "Other"),
  );

  return parts.length ? parts.join(" • ") : "No filters";
}

function toggleInSet<T>(s: Set<T>, v: T): Set<T> {
  const next = new Set(s);
  if (next.has(v)) next.delete(v);
  else next.add(v);
  // safety: never allow empty -> treat empty as "all"
  if (next.size === 0) return new Set(s);
  return next;
}

function triFromNullableBool(x: boolean | null): Tri {
  return x === true ? "yes" : x === false ? "no" : "unknown";
}

function segTypeFromReason(reason: string): SegType {
  const r = (reason || "").toLowerCase();
  if (r.includes("full rally")) return "full";
  if (r.includes("seg") || r.includes("segment")) return "partial";
  return "other";
}

function useAutoPauseWhenOffscreen(args: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  // how much of the video must be visible to allow play (0..1)
  visibleThreshold?: number;
  // if true, will attempt to resume when visible again
  autoResume?: boolean;
}) {
  const { videoRef, visibleThreshold = 0.35, autoResume = true } = args;

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Some browsers won't autoplay unless muted; you already set muted.
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isVisibleEnough =
          (entry?.intersectionRatio ?? 0) >= visibleThreshold;

        const vid = videoRef.current;
        if (!vid) return;

        if (!isVisibleEnough) {
          if (!vid.paused) vid.pause();
          return;
        }

        // visible enough
        if (!autoResume) return;
        if (vid.paused) {
          // play may be blocked if user hasn't interacted, so ignore errors
          try {
            void vid.play().catch(() => {});
          } catch {}
        }
      },
      {
        threshold: [0, visibleThreshold, 1],
        root: null, // viewport
      },
    );

    observer.observe(v);

    return () => observer.disconnect();
  }, [videoRef, visibleThreshold, autoResume]);
}

type Side = "near" | "far" | "unknown";

type ShotOverlayUsed =
  | "actions.shot"
  | "actions.ball"
  | "actions.ball_projection"
  | "hitter_point";

// type ShotNumberOverlay = {
//   shotNumber: number; // 1-based
//   playerId: number | null;
//     shotType: string | null;

//   atMsRel: number; // relative to segmentStartMs
//   u: number; // 0..1
//   v: number; // 0..1

//   showFromMsRel: number;
//   fullFromMsRel: number;
//   fadeStartMsRel: number;
//   hideAtMsRel: number;

//   frameIdx?: number;
//   used: ShotOverlayUsed;
//   usedConfidence: number | null;

//   featuresLabel?: string | null;
//   label: string | null;
//   followUv:
//     | Array<{
//         dtMs: number; // milliseconds after fullFromMsRel (shot time in segment clock)
//         u: number;
//         v: number;
//         used: "actions.ball" | "actions.ball_projection";
//         confidence: number | null;
//       }>
//     | undefined;
// };

type KitchenArrivalRow = {
  rallyIndex: number;

  // ✅ add this (server-provided stable point grouping key if you have it)
  pointKey: string | number | null;
  // ✅ PB Vision field name (exact)
  likely_correct_score?: boolean | null;

  startMs: number;
  endMs: number;

  numShots?: number;

  winnerTeam: 0 | 1 | null;
  winnerSide: Side;

  servingTeam: 0 | 1 | null;
  servingSide: Side;
  serveSpeedMPH: number | null;

  pointAwardedTeam: Team01 | null; // ✅ add
  pointAwardedToServer: boolean | null; // ✅ add

  servingArrivalSuccess: boolean | null;
  servingEarliestArrivalMsFromStart: number | null;
  servingHadOpportunity: boolean | null;

  segmentStartMs: number;
  segmentEndMs: number;
  segmentReason: string;

  videoUrl: string; // already computed by the report
  shotNumberOverlays?: ShotNumberOverlay[];
};

type RallyLocator =
  | string
  | { side?: "near" | "far"; lr?: "left" | "right"; descriptor?: string };

function formatLocator(locator: any): string {
  if (!locator) return "";
  if (typeof locator === "string") return locator;

  if (typeof locator === "object") {
    const side = typeof locator.side === "string" ? locator.side : "";
    const lr = typeof locator.lr === "string" ? locator.lr : "";
    const desc =
      typeof locator.descriptor === "string" && locator.descriptor
        ? ` • ${locator.descriptor}`
        : "";

    const core = [side, lr].filter(Boolean).join("-");
    return (core || "locator") + desc;
  }

  return String(locator);
}

type AiTurn = {
  id: string;
  createdAt?: number;
  savedAtMs?: number;
  question?: string;
  locator?: RallyLocator | null;
  result?: any;
  error?: any;
};

function rallyKey(r: {
  rallyIndex: number;
  segmentStartMs: number;
  segmentEndMs: number;
}): string {
  return `r${r.rallyIndex}_${r.segmentStartMs}_${r.segmentEndMs}`;
}

// -------------------------------
// Draggable dialog paper
// -------------------------------
function PaperComponent_old(props: PaperProps) {
  return (
    <Draggable
      handle="#kitchen-arrival-popup-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const PaperComponent = React.forwardRef<HTMLDivElement, PaperProps>(
  function PaperComponent(props, ref) {
    const nodeRef = React.useRef<HTMLDivElement>(null);

    // keep both refs happy: Draggable uses nodeRef; MUI gets the forwarded ref
    const handleRef = (el: HTMLDivElement | null) => {
      nodeRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref)
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    };

    return (
      <Draggable
        nodeRef={nodeRef}
        handle="#kitchen-arrival-popup-title"
        cancel={'[class*="MuiDialogContent-root"]'}
      >
        <Paper {...props} ref={handleRef} />
      </Draggable>
    );
  },
);

function WinnerSideLabel({ side }: { side: Side }) {
  return (
    <Box sx={{ fontWeight: 900, fontSize: 12, letterSpacing: 0.5 }}>
      {side === "near"
        ? "NEAR win"
        : side === "far"
          ? "FAR win"
          : "UNKNOWN win"}
    </Box>
  );
}

function CoachControls({
  videoRef,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
}) {
  const setRate = (rate: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = rate;
    try {
      void v.play();
    } catch {}
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      try {
        void v.play();
      } catch {}
    } else {
      v.pause();
    }
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
    >
      <Button
        size="small"
        variant="outlined"
        onClick={togglePlay}
        sx={{ textTransform: "none" }}
      >
        Play/Pause
      </Button>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {[0.25, 0.5, 0.75, 1].map((x) => (
          <Button
            key={x}
            size="small"
            variant="outlined"
            onClick={() => setRate(x)}
            sx={{ textTransform: "none", minWidth: 56, px: 1 }}
          >
            {x}x
          </Button>
        ))}
      </Box>
    </Box>
  );
}

// -------------------------------
// Server helpers (assumes your local server routes)
// -------------------------------
function normalizeBaseUrl(baseUrl?: string): string {
  const b = (baseUrl ?? "").trim();
  if (!b) return "";
  return b.endsWith("/") ? b.slice(0, -1) : b;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) throw new Error(`GET ${url} failed (${res.status})`);
  return (await res.json()) as T;
}

async function postJson<T>(url: string, body: any): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`POST ${url} failed (${res.status}) ${txt}`);
  }
  return (await res.json()) as T;
}

async function del(url: string): Promise<void> {
  const res = await fetch(url, { method: "DELETE" });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`DELETE ${url} failed (${res.status}) ${txt}`);
  }
}

// start influence

/** Which side/team the nav item is referring to in the current view */
export type TeamKey = "team0" | "team1";

/** Outcome buckets used by the ranked lists */
export type NavBucket =
  | "hurtsLost"
  | "hurtsSurvived"
  | "helpsWon"
  | "helpsButLost";

/** The kinds of nav items we can render */
export type NavItemKind = "special" | "pattern" | "header";

/** Optional phase for serve/receive disambiguation */
export type RallyPhase = "serve" | "return" | "rally" | "any";

/**
 * If you want “serve vs receive thing” to be explicit:
 * - "servingTeam": item applies when this team served
 * - "receivingTeam": item applies when this team received
 * - "any": item applies regardless
 */
export type ServeReceiveContext = "servingTeam" | "receivingTeam" | "any";

/**
 * Specials are *pinned* and should not be mixed into ranked buckets.
 * You can extend this list over time.
 */
export type SpecialKey =
  | "missedServe" // serve fault
  | "missedReturn" // return fault
  | "shortReturn" // return fault
  | "hitIntoNetToEndRally" // last shot into net
  | "hitOutLongToEndRally" // last shot long
  | "hitOutWideToEndRally" // last shot wide
  | "forcedFaultAnyShot"; // forced fault occurred (any shot)

/** How clicking should behave when adding to the filter list */
export type NavClickBehavior =
  | "addFilter" // normal add/merge into current filters
  | "singletonExact" // replace filters with ONLY this item (your “click means singleton exact”)
  | "toggle"; // toggle this item on/off in the current filter set

/** High-level filter semantics produced by a nav item */
export type NavFilterOp = "include" | "exclude";

/**
 * A simple filter DSL so each nav item can produce a deterministic filter.
 * Your app can interpret these however you already do filtering.
 */
export type NavFilterClause =
  | {
      field:
        | "specialKey"
        | "patternKey"
        | "phase"
        | "serveReceiveContext"
        | "winnerTeam"
        | "endReason";
      op: "eq" | "neq" | "in" | "nin";
      value: string | string[];
    }
  | {
      /** For advanced cases (range, predicates, etc.) */
      kind: "custom";
      id: string;
      payload?: unknown;
    };

export type NavFilterSpec = {
  op: NavFilterOp;
  clauses: NavFilterClause[];
};

/**
 * Shared fields across all items that render as a row/card.
 */
export type NavItemBase = {
  kind: NavItemKind;

  /** Stable unique key for React lists and deduping across sections */
  id: string;

  /** Which team panel this item belongs to */
  team: TeamKey;

  /** Display strings */
  title: string;
  subtitle?: string;

  /** Counts used in the UI */
  ralliesCount: number;

  /**
   * “delta” and “impact” are what you show like:
   *  -0.80  •  imp 2.40
   * You can treat them as optional for specials if not always computed.
   */
  delta?: number;
  impact?: number;

  /** Higher numbers appear earlier if you want to sort; specials can be pinned via huge rank */
  rank?: number;

  /** How the UI should treat click */
  clickBehavior?: NavClickBehavior;

  /** Filter this item should apply when selected */
  filter: NavFilterSpec;

  /**
   * Optional: for debugging or “more” expanders.
   * Keep anything here that shouldn’t be shown by default.
   */
  meta?: Record<string, unknown>;
};

/**
 * Header items (section titles like “Top Hurts & Lost”)
 * If you already render headers separately you may not need this.
 */
export type NavHeaderItem = NavItemBase & {
  kind: "header";
  /** e.g. "Top Hurts & Lost" */
  header: string;
  /** No filter for headers */
  filter: { op: "include"; clauses: [] };
};

/**
 * Specials: pinned-first section items.
 * They can optionally be serve/receive contextual.
 */
export type NavSpecialItem = NavItemBase & {
  kind: "special";
  specialKey: SpecialKey;

  /** Useful for explaining perceived duplicates */
  serveReceiveContext?: ServeReceiveContext;

  /** Optional phase */
  phase?: RallyPhase;
};

/**
 * Ranked “pattern” items that belong in one of the Top Hurts/Helps buckets.
 */
export type NavPatternItem = NavItemBase & {
  kind: "pattern";

  /** The bucket/section this pattern belongs to */
  bucket: NavBucket;

  /** Stable pattern key (e.g., "short_rally_le_5", "long_rally_ge_8") */
  patternKey: string;

  /** Optional tags for grouping/dedup */
  tags?: string[];

  /** Optional phase */
  phase?: RallyPhase;
};

/** Full union used by the navigator */
export type NavItem = NavHeaderItem | NavSpecialItem | NavPatternItem;

/** Helper: type guards */
export const isNavSpecialItem = (x: NavItem): x is NavSpecialItem =>
  x.kind === "special";
export const isNavPatternItem = (x: NavItem): x is NavPatternItem =>
  x.kind === "pattern";
export const isNavHeaderItem = (x: NavItem): x is NavHeaderItem =>
  x.kind === "header";

type InfluenceBucket = "hurtLost" | "hurtSurvived" | "helpedWon" | "helpedLost";
type InfluenceContext = "SERVE" | "RECEIVE";
type InfluenceImpact = "HELPED" | "HURT";

type InfluenceSpecialKey =
  | "missedServe"
  | "missedReturn"
  | "shortReturn"
  | "forcedFaultAnyShot"
  | "hitIntoNetToEndRally"
  | "hitOutLongToEndRally"
  | "hitOutWideToEndRally";

type InfluenceNavItem = {
  id: string;

  team: Team01;
  influencePick: InfluencePick;
  context: InfluenceContext; // SERVE / RECEIVE
  impact: InfluenceImpact; // HELPED / HURT

  featureKey: string;
  featureLabel: string;
  layman?: string;

  rallyIndicesToReview: number[];

  delta?: number;
  importance?: number;
  supportNote?: string;

  // ✅ NEW
  isSpecial?: boolean;
  specialKey?: InfluenceSpecialKey;

  bucketId: BucketId; // "hurt_lost" | "hurt_survived" | "help_won" | "help_lost"
  source: "serve" | "receive"; // optional but useful
};

type BucketId = "hurt_lost" | "hurt_survived" | "help_won" | "help_lost";

function fmtTeam(t: Team01) {
  return t === 0 ? "Team 0" : "Team 1";
}

function bucketTitle(b: BucketId) {
  switch (b) {
    case "hurt_lost":
      return "Top Hurts & Lost";
    case "hurt_survived":
      return "Top Hurts & Survived";
    case "help_won":
      return "Top Helps & Won";
    case "help_lost":
      return "Top Helps But Lost";
  }
}

function rowOutcomeForTeam(row: { winnerTeam: 0 | 1 | null }, team: Team01) {
  if (row.winnerTeam == null) return "UNKNOWN";
  return row.winnerTeam === team ? "WON" : "LOST";
}

/**
 * We need a way to decide WON/LOST for the influence’s team, per rally.
 * If you already have a rallyIndex->winnerTeam map in memory, pass it in.
 */

// function classifyBucket(args: {
//   item: InfluenceNavItem;
//   pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
// }): BucketId {
//   const { item, pointWinnerTeamByRallyIndex } = args;

//   let won = 0;
//   let lost = 0;

//   for (const ridx of item.rallyIndicesToReview) {
//     const w = pointWinnerTeamByRallyIndex[ridx];
//     if (w == null) continue;
//     if (w === item.team) won++;
//     else lost++;
//   }

//   const outcome =
//     won === 0 && lost === 0 ? "UNKNOWN" : won >= lost ? "WON" : "LOST";

//   if (item.impact === "HURT") {
//     if (outcome === "WON") return "hurt_survived";
//     return "hurt_lost";
//   }

//   // HELPED
//   if (outcome === "WON") return "help_won";
//   return "help_lost";
// }

function SummaryFactorLi(props: {
  it: InfluenceNavItem;
  gpText: string;
  onPick: (it: InfluenceNavItem) => void;
}) {
  const { it, gpText, onPick } = props;

  return (
    <li
      tabIndex={0}
      onClick={() => onPick(it)}
      onKeyDown={(e: any) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPick(it);
        }
      }}
      className="pbskills-pick-item"
      title={`${it.context}: ${it.featureLabel}`}
      style={{
        fontSize: 16,
        cursor: "pointer",
        userSelect: "none",
        display: "list-item",
      }}
    >
      {prettifyContext(it.context)} {prettifyLabel(it.featureLabel)}{" "}
      <span style={{ opacity: 0.8 }}>{gpText}</span>
    </li>
  );

  // return (
  //   <Box
  //     component="li"
  //     tabIndex={0}
  //     onClick={() => onPick(it)}
  //     onKeyDown={(e:any) => {
  //       if (e.key === "Enter" || e.key === " ") {
  //         e.preventDefault();
  //         onPick(it);
  //       }
  //     }}
  //     sx={{
  //       fontSize: 16,
  //       cursor: "pointer",
  //       userSelect: "none",
  //       display: "list-item",
  //       "&:hover": { textDecoration: "underline" },
  //       "&:focus-visible": {
  //         outline: "2px solid rgba(37, 99, 235, 0.55)",
  //         outlineOffset: 2,
  //         borderRadius: 1,
  //       },
  //     }}
  //     title={`${it.context}: ${it.featureLabel}`}
  //   >
  //     {prettifyContext(it.context)} {prettifyLabel(it.featureLabel)}{" "}
  //     <Box component="span" sx={{ opacity: 0.8 }}>
  //       {gpText}
  //     </Box>
  //   </Box>
  // );
}

export function InfluenceNavigatorMatrix(props: {
  items: InfluenceNavItem[];
  histogramComponent: ReactNode;
  subFilterComponent: ReactNode;

  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointKeyByRallyIndex: Record<number, string | number | null | undefined>; // ✅ add

  active: InfluenceNavItem | null;
  onPick: (it: InfluenceNavItem) => void;
  onClear: () => void;
}) {
  const {
    items,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    histogramComponent,
    subFilterComponent,
    pointKeyByRallyIndex,
  } = props;

  const [showAllFactors, setShowAllFactors] = React.useState(false);
  const team0Story = useMemo(
    () =>
      computeTopStoryForTeam({
        team: 0,
        picks: items, // <-- your array of picks you render for team0
        pointKeyByRallyIndex,
        pointWinnerTeamByRallyIndex,
      }),
    [items, pointKeyByRallyIndex, pointWinnerTeamByRallyIndex],
  );

  const team1Story = useMemo(
    () =>
      computeTopStoryForTeam({
        team: 1,
        picks: items,
        pointKeyByRallyIndex,
        pointWinnerTeamByRallyIndex,
      }),
    [items, pointKeyByRallyIndex, pointWinnerTeamByRallyIndex],
  );

  const gamePointTotals = React.useMemo(() => {
    return computeGamePointTotals({
      pointKeyByRallyIndex,
      pointWinnerTeamByRallyIndex,
    });
  }, [pointKeyByRallyIndex, pointWinnerTeamByRallyIndex]);

  const winnerTeam: Team01 | null =
    gamePointTotals.team0 === gamePointTotals.team1
      ? null
      : gamePointTotals.team0 > gamePointTotals.team1
        ? 0
        : 1;

  const matchSummary = React.useMemo(() => {
    if (winnerTeam == null) return null;

    const loserTeam: Team01 = winnerTeam === 0 ? 1 : 0;

    // Per-item point impact using your existing point logic
    const impacts = items.map((it) => {
      const c = getOutcomeCountsForTeamWithPoints({
        rallyIndices: it.rallyIndicesToReview ?? [],
        team: it.team,
        rallyWinnerTeamByRallyIndex,
        pointWinnerTeamByRallyIndex,
        pointKeyByRallyIndex,
      });
      return { it, c };
    });

    // Top "why winner won": winner’s items with highest pointsWon
    const topWinner = impacts
      .filter((x) => x.it.team === winnerTeam)
      .filter((x) => x.c.pointsWon > 0)
      .sort((a, b) => b.c.pointsWon - a.c.pointsWon);

    const topLoser = impacts
      .filter((x) => x.it.team === loserTeam)
      .filter((x) => x.c.pointsWon > 0)
      .sort((a, b) => b.c.pointsWon - a.c.pointsWon);

    // Top "why loser lost": loser’s items with highest pointsLost
    // (optionally focus on buckets that are inherently negative)
    const topLoserHurts = impacts
      .filter((x) => x.it.team === loserTeam)
      .filter((x) => x.c.pointsLost > 0)
      .sort((a, b) => b.c.pointsLost - a.c.pointsLost);

    const topWinnerHurts = impacts
      .filter((x) => x.it.team === winnerTeam)
      .filter((x) => x.c.pointsLost > 0)
      .sort((a, b) => b.c.pointsLost - a.c.pointsLost);

    return {
      winnerTeam,
      loserTeam,
      topWinner: topK(topWinner, 5),
      topLoser: topK(topLoser, 5),
      topLoserHurts: topK(topLoserHurts, 5),
      topWinnerHurts: topK(topWinnerHurts, 5),
    };
  }, [
    items,
    winnerTeam,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  ]);

  const byTeam = React.useMemo(() => {
    const out: Record<Team01, Record<BucketId, InfluenceNavItem[]>> = {
      0: { hurt_lost: [], hurt_survived: [], help_won: [], help_lost: [] },
      1: { hurt_lost: [], hurt_survived: [], help_won: [], help_lost: [] },
    };

    const specials: Record<Team01, InfluenceNavItem[]> = { 0: [], 1: [] };

    for (const it of items) {
      if (it.isSpecial) {
        specials[it.team].push(it);
        continue;
      }
      // const b = classifyBucket({ item: it, pointWinnerTeamByRallyIndex });
      // out[it.team][b].push(it);
      out[it.team][it.bucketId].push(it);
    }

    // sort specials (optional): more rallies, then importance, then abs(delta)
    const spSort = (a: InfluenceNavItem, b: InfluenceNavItem) => {
      const na = a.rallyIndicesToReview?.length ?? 0;
      const nb = b.rallyIndicesToReview?.length ?? 0;
      if (nb !== na) return nb - na;
      const ia = a.importance ?? 0;
      const ib = b.importance ?? 0;
      if (ib !== ia) return ib - ia;
      return Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0);
    };
    specials[0].sort(spSort);
    specials[1].sort(spSort);

    // existing bucket sort
    const sortFn = (a: InfluenceNavItem, b: InfluenceNavItem) => {
      const ia = a.importance ?? 0;
      const ib = b.importance ?? 0;
      if (ib !== ia) return ib - ia;
      const da = Math.abs(a.delta ?? 0);
      const db = Math.abs(b.delta ?? 0);
      return db - da;
    };

    for (const team of [0, 1] as Team01[]) {
      for (const b of [
        "hurt_lost",
        "hurt_survived",
        "help_won",
        "help_lost",
      ] as BucketId[]) {
        out[team][b].sort(sortFn);
      }
    }

    return { buckets: out, specials };
  }, [items, pointWinnerTeamByRallyIndex]);

// console.error("specials", byTeam.specials);
  return (
    <Box
      sx={{
        // border: "1px solid rgba(0,0,0,0.12)",
        // borderRadius: 2,
        // p: 1,
        mb: 1,
      }}
    >
      {/* <Box sx={{ mb: 1, fontSize: 13, opacity: 0.85 }}>
        <b>Team 0 Summary:</b> Helps → {team0Story.helpsWon.won} game points
        won, {team0Story.helpsLost.lost} game points lost; Hurts →{" "}
        {team0Story.hurtsSurvived.won} game points won,{" "}
        {team0Story.hurtsLost.lost} game points lost; Specials →{" "}
        {team0Story.specials.won} won / {team0Story.specials.lost} lost
      </Box>
      <Box sx={{ mb: 1, fontSize: 13, opacity: 0.85 }}>
        <b>Team 1 Summary:</b> Helps → {team0Story.helpsWon.won} game points
        won, {team1Story.helpsLost.lost} game points lost; Hurts →{" "}
        {team1Story.hurtsSurvived.won} game points won,{" "}
        {team1Story.hurtsLost.lost} game points lost; Specials →{" "}
        {team1Story.specials.won} won / {team0Story.specials.lost} lost
      </Box> */}

      {/* ✅ Match Summary */}
      {winnerTeam != null && matchSummary && (
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 1.5,
            border: "1px solid rgba(0,0,0,0.12)",
            fontSize: 13,
          }}
        >
          {/* <Box sx={{ fontWeight: 800, mb: 0.5 }}>
            Likely winner: {fmtTeam(matchSummary.winnerTeam)} — game points{" "}
            {gamePointTotals.team0}–{gamePointTotals.team1}
          </Box> */}

          <Box
            sx={{
              fontSize: 16,
              display: "flex",
              alignItems: "baseline",
              justifyContent: "flex-start",
              mb: 0.5,
              gap: 1, // small space between text and chip
            }}
          >
            <Box sx={{ fontWeight: 800, minWidth: 0 }}>
              Likely winner: {fmtTeam(matchSummary.winnerTeam)} — game points{" "}
              {gamePointTotals.team0}–{gamePointTotals.team1}
            </Box>

            <Chip
              size="medium"
              variant={showAllFactors ? "filled" : "outlined"}
              label={showAllFactors ? "Hide all factors" : "Show all factors"}
              onClick={() => setShowAllFactors((v) => !v)}
              sx={{
                //ml: 1, // ✅ “a little to the right”
                height: 22,
                fontSize: 16,
                flexShrink: 0, // prevent wrapping weirdness
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
            <Box sx={{ fontStyle: "italic" }}>
              {"Click any factor to filter to relevant rallies"}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 0.25,
            }}
          >
            {/* <Box sx={{ fontWeight: 850, fontSize: 12 }}>Team 0</Box> */}
          </Box>
          <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
            {/* LEFT */}
            <Box sx={{ fontSize: 16, minWidth: 420 }}>
              <b>Primary factors for {fmtTeam(matchSummary.winnerTeam)}:</b>
              <Box component="ul" sx={{ fontSize: 16, mt: 0.25, mb: 0, pl: 2 }}>
                {matchSummary.topWinner.map(({ it, c }) => (
                  <SummaryFactorLi
                    key={it.id}
                    it={it}
                    // ✅ match main table formatting
                    gpText={`(GP +${c.pointsWon}/-${c.pointsLost})`}
                    onPick={props.onPick}
                  />
                ))}
              </Box>
            </Box>
            {/* RIGHT */}
            <Box sx={{ fontSize: 16, minWidth: 420 }}>
              <b>Biggest problems for {fmtTeam(matchSummary.loserTeam)}:</b>
              <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2 }}>
                {matchSummary.topLoserHurts.map(({ it, c }) => (
                  <SummaryFactorLi
                    key={it.id}
                    it={it}
                    gpText={`(GP +${c.pointsWon}/-${c.pointsLost})`}
                    onPick={props.onPick}
                  />
                ))}
              </Box>
            </Box>
            {histogramComponent ? (
              <Box sx={{ display: "flex", transform: "translateY(-30px)" }}>
                {histogramComponent}
              </Box>
            ) : null}
          </Box>

          <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
            {/* LEFT */}
            <Box sx={{ fontSize: 16, minWidth: 420 }}>
              <b>Primary factors for {fmtTeam(matchSummary.loserTeam)}:</b>
              <Box component="ul" sx={{ fontSize: 16, mt: 0.25, mb: 0, pl: 2 }}>
                {matchSummary.topLoser.map(({ it, c }) => (
                  <SummaryFactorLi
                    key={it.id}
                    it={it}
                    // ✅ match main table formatting
                    gpText={`(GP +${c.pointsWon}/-${c.pointsLost})`}
                    onPick={props.onPick}
                  />
                ))}
              </Box>
            </Box>
            {/* RIGHT */}
            <Box sx={{ fontSize: 16, minWidth: 420 }}>
              <b>Biggest problems for {fmtTeam(matchSummary.winnerTeam)}:</b>
              <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2 }}>
                {matchSummary.topWinnerHurts.map(({ it, c }) => (
                  <SummaryFactorLi
                    key={it.id}
                    it={it}
                    gpText={`(GP +${c.pointsWon}/-${c.pointsLost})`}
                    onPick={props.onPick}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {/* <Box sx={{ display: "inline-block", marginLeft: "30px" }}>
            <b>Biggest problems for {fmtTeam(matchSummary.loserTeam)}:</b>
            {matchSummary.topLoserHurts.map(({ it, c }) => (
              <Box
                key={it.id}
                component="li"
                tabIndex={0}
                onClick={() => props.onPick(it)} // ✅ EXACT same handler path
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    props.onPick(it);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { textDecoration: "underline" },
                  "&:focus-visible": {
                    outline: "2px solid rgba(37, 99, 235, 0.55)",
                    outlineOffset: 2,
                    borderRadius: 1,
                  },
                }}
                title={`${it.context}: ${it.featureLabel}`}
              >
                {prettifyContext(it.context)} {prettifyLabel(it.featureLabel)}{" "}
                <Box component="span" sx={{ opacity: 0.8 }}>
                  ({c.pointsLost} GP − / {c.pointsWon} GP +)
                </Box>
              </Box>
            ))}{" "}
          </Box> */}
        </Box>
      )}

      {/* three columns */}

      {showAllFactors ? (
        <>
          <Box
            sx={{
              marginLeft: "480px",
              marginBottom: "6px",
              fontStyle: "italic",
            }}
          >
            {"Click any factor to filter to relevant rallies"}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            {[0, 1].map((team) => {
              const teamKey = team as Team01;

              return (
                <TeamColumn
                  key={teamKey}
                  team={teamKey}
                  buckets={byTeam.buckets[teamKey]}
                  specials={byTeam.specials[teamKey]}
                  rallyWinnerTeamByRallyIndex={rallyWinnerTeamByRallyIndex}
                  pointWinnerTeamByRallyIndex={pointWinnerTeamByRallyIndex}
                  pointKeyByRallyIndex={props.pointKeyByRallyIndex}
                  activeId={props.active?.id ?? null}
                  onPick={props.onPick}
                />
              );
            })}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                alignItems: "stretch",
              }}
            >
              {subFilterComponent}
              {/* {histogramComponent} */}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ fontSize: 16, fontStyle: "italic", pl: 0.5, py: 0.5 }}>
          <Chip
            size="medium"
            variant={showAllFactors ? "filled" : "outlined"}
            label={showAllFactors ? "Hide all factors" : "Show all factors"}
            onClick={() => setShowAllFactors((v) => !v)}
            sx={{
              //ml: 1, // ✅ “a little to the right”
              height: 22,
              fontSize: 14,
              flexShrink: 0, // prevent wrapping weirdness
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

function TeamColumn(props: {
  team: Team01;
  buckets: Record<BucketId, InfluenceNavItem[]>;
  specials: InfluenceNavItem[];
  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointKeyByRallyIndex: Record<number, string | number | null | undefined>; // ✅ add
  activeId: string | null;
  onPick: (it: InfluenceNavItem) => void;
}) {
  const {
    team,
    buckets,
    specials,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  } = props;

  return (
    <Box
      sx={{
        //flex: 1,
        minWidth: 500,
        px: 0.25,
        // border: "1px solid rgba(0,0,0,0.10)",
        // borderRadius: 2,
        // p: 1,
      }}
    >
      <Box sx={{ fontWeight: 900, fontSize: 13, mb: 1 }}>{fmtTeam(team)}</Box>

      {/* ✅ pinned specials */}
      <BucketSection
        // title="Special"
        title={specialsTitleWithCounts({
          team,
          specials,
          rallyWinnerTeamByRallyIndex: props.rallyWinnerTeamByRallyIndex,
          pointWinnerTeamByRallyIndex: props.pointWinnerTeamByRallyIndex,
          pointKeyByRallyIndex,
        })}
        items={specials}
        activeId={props.activeId}
        onPick={props.onPick}
        team={team}
        rallyWinnerTeamByRallyIndex={rallyWinnerTeamByRallyIndex}
        pointWinnerTeamByRallyIndex={pointWinnerTeamByRallyIndex}
        pointKeyByRallyIndex={pointKeyByRallyIndex}
      />

      {(
        ["hurt_lost", "hurt_survived", "help_won", "help_lost"] as BucketId[]
      ).map((b) => (
        <BucketSection
          key={b}
          // title={bucketTitle(b)}
          title={bucketTitleWithCounts({
            title: bucketTitle(b),
            bucketId: b,
            team,
            items: buckets[b] ?? [],
            rallyWinnerTeamByRallyIndex: props.rallyWinnerTeamByRallyIndex,
            pointWinnerTeamByRallyIndex: props.pointWinnerTeamByRallyIndex,
            pointKeyByRallyIndex,
          })}
          items={buckets[b] ?? []}
          activeId={props.activeId}
          onPick={props.onPick}
          team={team}
          rallyWinnerTeamByRallyIndex={props.rallyWinnerTeamByRallyIndex}
          pointWinnerTeamByRallyIndex={props.pointWinnerTeamByRallyIndex}
          pointKeyByRallyIndex={pointKeyByRallyIndex}
        />
      ))}
    </Box>
  );
}

function whoTeam(teamOfEvent: 0 | 1, columnTeam: 0 | 1) {
  return teamOfEvent === columnTeam ? "US" : "OPP";
}

export function prettifyContext(raw: string): string {
  let s = String(raw ?? "");

  if (raw === "RECEIVE") {
    s = "Receiving: ";
  }
  if (raw === "SERVE") {
    s = "Serving: ";
  }

  return s;
}

export function prettifyLabel(raw: string): string {
  let s = String(raw ?? "");

  // 1) Remove the standalone token "US"
  //    - preserves spacing nicely
  s = s.replace(/\bUS\b\s*/g, "");

  // Replace shot range (exact string match after dash normalization)
  // s = s.replaceAll("(shots 0-4)", "(shots 1-5)");
  // replace (shots 0–4) OR (shots 0-4) -> (shots 1-5)
  s = s.replace(/\(shots\s*0\s*[-–]\s*4\)/i, "(shots 1-5)");

  // 2) If the label starts with "Team", remove it and capitalize the next word
  //    Handles: "Team foo", "Team: foo", "Team - foo"
  s = s.replace(/^\s*Team\s*[:\-]?\s+/i, "");

  // Capitalize first character of what's left (i.e., the "next word")
  s = s.replace(/^\s*([a-z])/, (m, c: string) => c.toUpperCase());

  // 3) Clean up any doubled spaces
  s = s.replace(/\s{2,}/g, " ").trim();

// console.error({ raw, s });

  return s;
}

function BucketSection(props: {
  title: string;
  items: InfluenceNavItem[];
  activeId: string | null;
  onPick: (it: InfluenceNavItem) => void;
  team: Team01;
  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
}) {
  const top = props.items;

  const COUNT_COL_PX = 22; // enough for 0–99
  const LABEL_COL_PX = 560; // tweak (220–320)

  // Sort highest count first (your prior behavior)
  const sorted = top
    .slice()
    .sort(
      (a, b) =>
        -(a.rallyIndicesToReview.length - b.rallyIndicesToReview.length),
    );

  const getRowSx = (selected: boolean) => ({
    display: "table-row",
    cursor: "pointer",
    backgroundColor: selected ? "rgba(37, 99, 235, 0.10)" : "#fff",
    transition: "background-color 120ms ease",

    "&:hover": {
      backgroundColor: selected
        ? "rgba(37, 99, 235, 0.14)"
        : "rgba(0,0,0,0.04)",
    },

    // optional: make the label feel “link-like” on hover
    "&:hover .rowLabel": {
      textDecoration: "underline",
    },

    // optional: slightly emphasize the right count on hover
    "&:hover .rowCount": {
      fontWeight: 700,
    },

    // keyboard focus
    "&:focus-visible": {
      outline: "2px solid rgba(37, 99, 235, 0.55)",
      outlineOffset: -2,
    },
  });

  return (
    <Box sx={{ mb: 0.25 }}>
      <Box sx={{ fontWeight: 850, fontSize: 12, mb: 0 }}>{props.title}</Box>

      {sorted.length === 0 ? (
        <Box sx={{ fontSize: 12, pl: 0.5 }}>—</Box>
      ) : (
        <Box
          sx={{
            // lightweight table layout (no MUI Table components)
            display: "inline-table",
            width: "auto",
            borderCollapse: "collapse",
          }}
        >
          {sorted
            .sort((a, b) => a.context.localeCompare(b.context))
            .map((it) => {
              const selected = props.activeId === it.id;
              const n = it.rallyIndicesToReview?.length ?? 0;

              const c = getOutcomeCountsForTeamWithPoints({
                rallyIndices: it.rallyIndicesToReview ?? [],
                team: props.team,
                rallyWinnerTeamByRallyIndex: props.rallyWinnerTeamByRallyIndex,
                pointWinnerTeamByRallyIndex: props.pointWinnerTeamByRallyIndex,
                pointKeyByRallyIndex: props.pointKeyByRallyIndex,
              });

              // console.error("row...", { it });

              return (
                <Box
                  key={it.id}
                  tabIndex={0}
                  onClick={() => props.onPick(it)}
                  sx={getRowSx(selected)}
                >
                  <Box
                    sx={{
                      display: "table-cell",
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                      fontWeight: "normal",
                      verticalAlign: "middle",
                      width: LABEL_COL_PX,
                      maxWidth: LABEL_COL_PX,

                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={`${it.context}: ${it.featureLabel}`}
                  >
                    {prettifyContext(it.context) +
                      // " " +
                      // whoTeam(it.team, props.team) +
                      " " +
                      prettifyLabel(it.featureLabel)}
                  </Box>
                  {/* <Box
                    component="span"
                    sx={{ opacity: 0.6, ml: 1, fontSize: 12 }}
                  >
                    [{it.team}] {it.featureKey} bucket={it.bucketId} impact=
                    {it.impact} delta={(it.delta ?? 0).toFixed(3)}
                  </Box> */}

                  <Box
                    sx={{
                      display: "table-cell",
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                      textAlign: "right",
                      width: COUNT_COL_PX, // shrink-to-fit
                    }}
                  >
                    {/* {`${n}`} */}
                    {`${n} (GP +${c.pointsWon}/-${c.pointsLost})`}
                  </Box>
                </Box>
              );
            })}
        </Box>
      )}
    </Box>
  );
}

// end influence

function getOutcomeCountsForTeamWithPoints(args: {
  rallyIndices: number[];
  team: 0 | 1;

  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
}) {
  const {
    rallyIndices,
    team,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  } = args;

  // -------------------
  // Rally-level counts
  // -------------------
  let ralliesWon = 0;
  let ralliesLost = 0;
  let ralliesUnk = 0;

  for (let i = 0; i < rallyIndices.length; i++) {
    const ridx = rallyIndices[i];
    const w = rallyWinnerTeamByRallyIndex[ridx];

    if (w === 0 || w === 1) {
      if (w === team) ralliesWon++;
      else ralliesLost++;
    } else {
      ralliesUnk++;
    }
  }

  // -------------------
  // Point-level counts
  // -------------------
  // IMPORTANT:
  // - pointKey may exist even when NO game point was awarded (e.g. sideout grouping)
  // - Only count a "point" if pointWinnerTeamByRallyIndex[ridx] is 0/1 (i.e., an awarded point)
  // - De-dupe by pointKey so multiple rallies tied to the same pointKey count once
  const pointWinnerByKey: Record<string, 0 | 1> = Object.create(null);

  for (let i = 0; i < rallyIndices.length; i++) {
    const ridx = rallyIndices[i];

    const pkRaw = pointKeyByRallyIndex[ridx];
    if (pkRaw == null) continue;

    const pw = pointWinnerTeamByRallyIndex[ridx];

    // ✅ If no point was awarded on this rally, do NOT count it as a point.
    if (pw !== 0 && pw !== 1) continue;

    const pk = String(pkRaw);

    // ✅ De-dupe: first seen wins (they should all agree anyway under this definition)
    if (!(pk in pointWinnerByKey)) {
      pointWinnerByKey[pk] = pw;
    }
  }

  let pointsWon = 0;
  let pointsLost = 0;
  const pointsUnk = 0; // by construction, we only include known 0/1 winners

  const keys = Object.keys(pointWinnerByKey);
  for (let j = 0; j < keys.length; j++) {
    const k = keys[j];
    const w = pointWinnerByKey[k];

    if (w === team) pointsWon++;
    else pointsLost++;
  }

  return {
    ralliesWon,
    ralliesLost,
    ralliesUnk,
    pointsWon,
    pointsLost,
    pointsUnk,
  };
}

function getOutcomeCountsForTeamWithPoints_oldoldold(args: {
  rallyIndices: number[];
  team: 0 | 1;

  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
}) {
  const {
    rallyIndices,
    team,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  } = args;

  // -------------------
  // Rally-level counts
  // -------------------
  let ralliesWon = 0;
  let ralliesLost = 0;
  let ralliesUnk = 0;

  for (let i = 0; i < rallyIndices.length; i++) {
    const ridx = rallyIndices[i];
    const w = rallyWinnerTeamByRallyIndex[ridx];
    if (w == null) ralliesUnk++;
    else if (w === team) ralliesWon++;
    else ralliesLost++;
  }

  // -------------------
  // Point-level counts
  // -------------------
  // Only count *real* points (those with a pointKey).
  // De-dupe by pointKey, and allow "unknown -> known" upgrade.
  const pointWinnerByKey: Record<string, 0 | 1 | null> = Object.create(null);

  for (let i = 0; i < rallyIndices.length; i++) {
    const ridx = rallyIndices[i];
    const pkRaw = pointKeyByRallyIndex[ridx];

    // ✅ Not a scored game point → do not count
    if (pkRaw == null) continue;

    const pk = String(pkRaw);
    const pw = pointWinnerTeamByRallyIndex[ridx];

    if (!(pk in pointWinnerByKey)) {
      // first time we've seen this point
      pointWinnerByKey[pk] = pw == null ? null : pw;
    } else {
      // upgrade unknown -> known if we later learn the winner for same pointKey
      if (pointWinnerByKey[pk] == null && pw != null) {
        pointWinnerByKey[pk] = pw;
      }
    }
  }

  let pointsWon = 0;
  let pointsLost = 0;
  let pointsUnk = 0;

  const keys = Object.keys(pointWinnerByKey);
  for (let j = 0; j < keys.length; j++) {
    const k = keys[j];
    const w = pointWinnerByKey[k];

    if (w == null) pointsUnk++;
    else if (w === team) pointsWon++;
    else pointsLost++;
  }

  return {
    ralliesWon,
    ralliesLost,
    ralliesUnk,
    pointsWon,
    pointsLost,
    pointsUnk,
  };
}

function getOutcomeCountsForTeamWithPoints_old(args: {
  rallyIndices: number[];
  team: 0 | 1;

  // ✅ split maps
  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;

  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
}) {
  const {
    rallyIndices,
    team,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  } = args;

  // -------------------
  // Rally-level counts
  // -------------------
  let ralliesWon = 0;
  let ralliesLost = 0;
  let ralliesUnk = 0;

  for (let i = 0; i < rallyIndices.length; i++) {
    const ridx = rallyIndices[i];
    const w = rallyWinnerTeamByRallyIndex[ridx];

    if (w == null) ralliesUnk++;
    else if (w === team) ralliesWon++;
    else ralliesLost++;
  }

  // -------------------
  // Point-level counts
  // -------------------
  // Map pointKey -> winnerTeam (point winner)
  const pointWinnerByKey: Record<string, 0 | 1 | null> = Object.create(null);
  const pointSeen: Record<string, true> = Object.create(null);
  const seenPointKeys = new Set<string>();

  for (let i = 0; i < rallyIndices.length; i++) {
    const ridx = rallyIndices[i];

    const pkRaw = pointKeyByRallyIndex[ridx];

    // ✅ If this rally did not correspond to an actual game point, do NOT count it as a point.
    if (pkRaw == null) continue;

    const pk = String(pkRaw);
    if (seenPointKeys.has(pk)) continue;
    seenPointKeys.add(pk);

    const key = String(pkRaw != null ? pkRaw : ridx);

    const pw = pointWinnerTeamByRallyIndex[ridx];

    if (!pointSeen[key]) {
      pointSeen[key] = true;
      pointWinnerByKey[key] = pw == null ? null : pw;
    } else {
      // If previously unknown but now known, upgrade
      if (pointWinnerByKey[key] == null && pw != null) {
        pointWinnerByKey[key] = pw;
      }
    }
  }

  let pointsWon = 0;
  let pointsLost = 0;
  let pointsUnk = 0;

  const keys = Object.keys(pointWinnerByKey);
  for (let j = 0; j < keys.length; j++) {
    const k = keys[j];
    const w = pointWinnerByKey[k];

    if (w == null) pointsUnk++;
    else if (w === team) pointsWon++;
    else pointsLost++;
  }

  return {
    ralliesWon,
    ralliesLost,
    ralliesUnk,
    pointsWon,
    pointsLost,
    pointsUnk,
  };
}

function unionRallyIndices(items: InfluenceNavItem[]): number[] {
  const s = new Set<number>();
  for (const it of items) {
    for (const ridx of it.rallyIndicesToReview ?? []) s.add(ridx);
  }
  return Array.from(s);
}

function bucketOutcomeKind(b: BucketId): "won" | "lost" {
  // which number should appear in the header
  switch (b) {
    case "hurt_survived":
    case "help_won":
      return "won";
    case "hurt_lost":
    case "help_lost":
      return "lost";
  }
}

function bucketTitleWithCounts(args: {
  title: string;
  bucketId: BucketId;
  team: Team01;
  items: InfluenceNavItem[];

  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;

  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
}) {
  const {
    title,
    bucketId,
    team,
    items,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  } = args;

  const union = unionRallyIndices(items);

  const c = getOutcomeCountsForTeamWithPoints({
    rallyIndices: union,
    team,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  });

  const k = bucketOutcomeKind(bucketId);

  if (k === "won") {
    return `${title} (${c.ralliesWon} rall${
      c.ralliesWon === 1 ? "y" : "ies"
    } won; ${c.pointsWon} game point${c.pointsWon === 1 ? "" : "s"} won)`;
  }
  return `${title} (${c.ralliesLost} rall${
    c.ralliesLost === 1 ? "y" : "ies"
  } lost; ${c.pointsLost} game point${c.pointsLost === 1 ? "" : "s"} lost)`;
}

function specialsTitleWithCounts(args: {
  team: Team01;
  specials: InfluenceNavItem[];

  rallyWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;

  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
}) {
  const {
    team,
    specials,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  } = args;

  const union = unionRallyIndices(specials);
  const totalRallies = union.length;

  const c = getOutcomeCountsForTeamWithPoints({
    rallyIndices: union,
    team,
    rallyWinnerTeamByRallyIndex,
    pointWinnerTeamByRallyIndex,
    pointKeyByRallyIndex,
  });

  // const rallyParts = [`${c.ralliesWon}W`, `${c.ralliesLost}L`];
  // if (c.ralliesUnk) rallyParts.push(`${c.ralliesUnk}?`);

  // const pointParts = [`${c.pointsWon}W`, `${c.pointsLost}L`];
  // if (c.pointsUnk) pointParts.push(`${c.pointsUnk}?`);

  // const typesCount = specials.length;
  // const totalPoints = c.pointsWon + c.pointsLost + c.pointsUnk;

  // return `Specials (${typesCount} types; ${totalRallies} rallies: ${rallyParts.join(
  //   " / "
  // )}; ${totalPoints} points: ${pointParts.join(" / ")})`;

  const totalPoints = c.pointsWon + c.pointsLost + c.pointsUnk;

  // ✅ Match the “(X rallies ...; Y points ...)” style used elsewhere — no W/L shorthand.
  // return `Specials (${totalRallies} rallies; ${totalPoints} points)`;

  return `Specials (${totalRallies} rall${totalRallies === 1 ? "y" : "ies"}: ${
    c.ralliesWon
  } won / ${c.ralliesLost} lost; ${totalPoints} game point${
    totalPoints === 1 ? "" : "s"
  }: ${c.pointsWon} won / ${c.pointsLost} lost)`;

  // const typesCount = specials.length;

  //   const ralliesSuffix =
  //     c.ralliesUnk ? `; ${c.ralliesUnk} rallies unknown` : "";

  //   const pointsSuffix =
  //     c.pointsUnk ? `; ${c.pointsUnk} points unknown` : "";

  //   // return `Specials (${typesCount} types; ` +
  //   //   `${totalRallies} rallies: ${c.ralliesWon} won / ${c.ralliesLost} lost${ralliesSuffix}; ` +
  //   //   `${c.pointsWon + c.pointsLost + c.pointsUnk} points: ${c.pointsWon} won / ${c.pointsLost} lost${pointsSuffix})`;

  //   return `Specials (` +
  //     `${totalRallies} rallies: ${c.ralliesWon} won / ${c.ralliesLost} lost${ralliesSuffix}; ` +
  //     `${c.pointsWon + c.pointsLost + c.pointsUnk} points: ${c.pointsWon} won / ${c.pointsLost} lost${pointsSuffix})`;
}

// type Team01 = 0 | 1;

type PickLike = {
  bucketId: string; // e.g. "help_won", "help_lost", "hurt_survived", "hurt_lost", etc
  isSpecial?: boolean;
  rallyIndicesToReview?: number[];
};

function rollupGamePointsForPickGroup(args: {
  team: Team01;
  picks: InfluenceNavItem[];
  include: (p: PickLike) => boolean;

  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, Team01 | null | undefined>;
}) {
  const {
    team,
    picks,
    include,
    pointKeyByRallyIndex,
    pointWinnerTeamByRallyIndex,
  } = args;

  const keys = new Set<string>();
  const winnerByKey: Record<string, Team01 | null> = Object.create(null);

  for (const p of picks) {
    if (!include(p)) continue;

    const rixs = p.rallyIndicesToReview ?? [];
    for (const ridx of rixs) {
      const pkRaw = pointKeyByRallyIndex[ridx];
      if (pkRaw == null) continue; // not a scored game point

      const pk = String(pkRaw);

      // de-dupe by pointKey, but allow unknown->known upgrade
      const pw = pointWinnerTeamByRallyIndex[ridx] ?? null;

      if (!keys.has(pk)) {
        keys.add(pk);
        winnerByKey[pk] = pw;
      } else {
        if (winnerByKey[pk] == null && pw != null) winnerByKey[pk] = pw;
      }
    }
  }

  let won = 0;
  let lost = 0;
  let unk = 0;

  for (const pk of Object.keys(winnerByKey)) {
    const w = winnerByKey[pk];
    if (w == null) unk++;
    else if (w === team) won++;
    else lost++;
  }

  return { total: won + lost + unk, won, lost, unk };
}

function computeGamePointTotals(args: {
  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, 0 | 1 | null | undefined>;
}) {
  const { pointKeyByRallyIndex, pointWinnerTeamByRallyIndex } = args;

  // De-dupe by pointKey, only count awarded points (winner is 0/1)
  const pointWinnerByKey: Record<string, 0 | 1> = Object.create(null);

  const rallyIndices = Object.keys(pointKeyByRallyIndex).map((k) => Number(k));

  for (const ridx of rallyIndices) {
    const pkRaw = pointKeyByRallyIndex[ridx];
    if (pkRaw == null) continue;

    const pw = pointWinnerTeamByRallyIndex[ridx];
    if (pw !== 0 && pw !== 1) continue;

    const pk = String(pkRaw);
    if (!(pk in pointWinnerByKey)) {
      pointWinnerByKey[pk] = pw;
    }
  }

  let team0 = 0;
  let team1 = 0;

  for (const pk of Object.keys(pointWinnerByKey)) {
    const w = pointWinnerByKey[pk];
    if (w === 0) team0++;
    else team1++;
  }

  return { team0, team1, total: team0 + team1 };
}

function topK<T>(arr: T[], k: number) {
  if (arr.length <= k) return arr;
  return arr.slice(0, k);
}

function computeTopStoryForTeam(args: {
  team: Team01;
  picks: InfluenceNavItem[];
  pointKeyByRallyIndex: Record<number, string | number | null | undefined>;
  pointWinnerTeamByRallyIndex: Record<number, Team01 | null | undefined>;
}) {
  const { team, picks, pointKeyByRallyIndex, pointWinnerTeamByRallyIndex } =
    args;

  const helpsWon = rollupGamePointsForPickGroup({
    team,
    picks,
    include: (p) => p.bucketId === "help_won",
    pointKeyByRallyIndex,
    pointWinnerTeamByRallyIndex,
  });

  const helpsLost = rollupGamePointsForPickGroup({
    team,
    picks,
    include: (p) => p.bucketId === "help_lost",
    pointKeyByRallyIndex,
    pointWinnerTeamByRallyIndex,
  });

  const hurtsSurvived = rollupGamePointsForPickGroup({
    team,
    picks,
    include: (p) => p.bucketId === "hurt_survived",
    pointKeyByRallyIndex,
    pointWinnerTeamByRallyIndex,
  });

  const hurtsLost = rollupGamePointsForPickGroup({
    team,
    picks,
    include: (p) => p.bucketId === "hurt_lost",
    pointKeyByRallyIndex,
    pointWinnerTeamByRallyIndex,
  });

  const specials = rollupGamePointsForPickGroup({
    team,
    picks,
    include: (p) => p.isSpecial === true || p.bucketId === "specials",
    pointKeyByRallyIndex,
    pointWinnerTeamByRallyIndex,
  });

  return { helpsWon, helpsLost, hurtsSurvived, hurtsLost, specials };
}

// -------------------------------
// Main popup
// -------------------------------
export function RallyPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  baseUrl?: string;
}) {
  const [showTop, setShowTop] = React.useState(true);

  const [shotRange, setShotRange] = useState<[number, number]>([0, 999]);
  const shotRangeTouchedRef = React.useRef(false);

  const setShotRangeFromHistogram = React.useCallback(
    (next: [number, number]) => {
      shotRangeTouchedRef.current = true;
      setShotRange(next);
    },
    [],
  );

  const baseUrl = normalizeBaseUrl(props.baseUrl);

  const [influenceReport, setInfluenceReport] =
    useState<SideoutGameInfluenceReport | null>(null);

  // “active navigation”
  const [activeInfluence, setActiveInfluence] =
    useState<InfluenceNavItem | null>(null);

  // which item in the review list we’re currently on
  const [activeReviewIdx, setActiveReviewIdx] = useState(0);

  // map rallyIndex -> DOM node so we can scroll/jump
  const rallyCardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  function outcomeForTeamInRally(
    row: KitchenArrivalRow,
    team: Team01,
  ): "WON" | "LOST" | "UNKNOWN" {
    if (row.winnerTeam == null) return "UNKNOWN";
    return row.winnerTeam === team ? "WON" : "LOST";
  }

  function influenceLabel(i: InfluenceNavItem): string {
    // concise “Viewing because …”
    const ctx = i.context === "SERVE" ? "Serve" : "Receive";
    const imp = i.impact === "HELPED" ? "Helped" : "Hurt";
    return `${fmtTeam(i.team)} • ${ctx} • ${imp} • ${i.featureLabel}`;
  }

  const [influenceNav, setInfluenceNav] = useState<{
    team: Team01;
    context: InfluenceContext;
    bucket: InfluenceBucket;
    selectedIndex: number;
  }>({
    team: 0,
    context: "RECEIVE",
    bucket: "hurtLost",
    selectedIndex: 0,
  });

  const activeInfluenceRallyIndexSet = useMemo(() => {
    if (!activeInfluence) return null;
    return new Set(activeInfluence.rallyIndicesToReview ?? []);
  }, [activeInfluence]);

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  const [rows, setRows] = useState<KitchenArrivalRow[]>([]);
  const [turnsByKey, setTurnsByKey] = useState<Record<string, AiTurn[]>>({});

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const setTurnsForKey = (key: string, turns: AiTurn[]) => {
    setTurnsByKey((prev) => ({ ...prev, [key]: turns }));
  };

  const shotBounds = useMemo(() => {
    const vals = (rows ?? [])
      .map((r) => (typeof r.numShots === "number" ? r.numShots : null))
      .filter((x): x is number => typeof x === "number");

    if (!vals.length) return null;
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [rows]);

  const fullShotRange: [number, number] | null = shotBounds
    ? [shotBounds.min, shotBounds.max]
    : null;

  const resetShotRangeToFull = React.useCallback(() => {
    if (fullShotRange) setShotRange(fullShotRange);
  }, [fullShotRange]);

  React.useEffect(() => {
    if (!props.open) return;

    // reset every time you open
    shotRangeTouchedRef.current = false;
    // setShotRange([0, 999]);

    setInfluenceReport(null);
    setInfluenceNav({
      team: 0,
      context: "RECEIVE",
      bucket: "hurtLost",
      selectedIndex: 0,
    });

    // ... your existing load logic ...
  }, [props.open, props.packageId]);

  function uniqSorted(nums: number[]): number[] {
    return Array.from(new Set(nums))
      .filter((n) => typeof n === "number")
      .sort((a, b) => a - b);
  }

  /**
   * Works for BOTH:
   * - TriagePick: { ralliesToReview: [{ rallyIndex: number }] }
   * - SideoutInfluenceRow: { trueRallyIndices, trueGoodOutcomeRallyIndices, trueBadOutcomeRallyIndices, ... }
   */
  function extractRallyIndicesFromPick(p: any): number[] {
    if (!p) return [];

    // ✅ New triage shape
    if (Array.isArray(p.ralliesToReview)) {
      const xs = p.ralliesToReview
        .map((r: any) =>
          typeof r?.rallyIndex === "number" ? r.rallyIndex : null,
        )
        .filter((x: any): x is number => x != null);
      if (xs.length) return uniqSorted(xs);
    }

    // ✅ Influence-row shapes
    const candidates = [
      p.trueRallyIndices,
      p.trueGoodOutcomeRallyIndices,
      p.trueBadOutcomeRallyIndices,
      p.falseRallyIndices,
      p.falseGoodOutcomeRallyIndices,
      p.falseBadOutcomeRallyIndices,
      p.unknownRallyIndices,
      // Back-compat
      p.rallyIndicesToReview,
      p.rallyIndices,
      p.indices,
    ];

    for (const c of candidates) {
      if (Array.isArray(c)) {
        const xs = c
          .map((x: any) => (typeof x === "number" ? x : null))
          .filter((x: any): x is number => x != null);
        if (xs.length) return uniqSorted(xs);
      }
    }

    return [];
  }

  function extractRallyIndicesFromPick_old(p: any): number[] {
    if (!p) return [];

    // NEW canonical shape: ralliesToReview: [{ rallyIndex, ... }]
    const r2r = p.ralliesToReview;
    if (Array.isArray(r2r)) {
      const xs = r2r
        .map((x) => (typeof x?.rallyIndex === "number" ? x.rallyIndex : null))
        .filter((x): x is number => x != null);
      if (xs.length) return Array.from(new Set(xs)).sort((a, b) => a - b);
    }

    // Back-compat fallbacks
    const candidates: any[] = [
      p.rallyIndicesToReview,
      p.rallyIndices,
      p.trueRallyIndices,
      p.falseRallyIndices,
      p.indices,
    ];

    for (const c of candidates) {
      if (Array.isArray(c)) {
        const xs = c
          .map((x) => (typeof x === "number" ? x : null))
          .filter((x): x is number => x != null);
        if (xs.length) return Array.from(new Set(xs)).sort((a, b) => a - b);
      }
    }

    return [];
  }

  function extractRallyInd_old(p: any): number[] {
    // Try multiple field names
    const candidates = [
      p?.ralliesToReview,
      p?.rallyIndicesToReview,
      p?.rallyIndices,
      p?.rally_indices,
      p?.rallies_to_review,
      p?.rallies, // sometimes people put raw indices here
    ];

    for (const c of candidates) {
      if (!Array.isArray(c)) continue;

      const out = c
        .map((rr: any) => {
          // allow: number | string | {rallyIndex} | {rally_index}
          if (typeof rr === "number") return rr;
          if (typeof rr === "string") {
            const n = Number(rr);
            return Number.isFinite(n) ? n : null;
          }
          const v =
            rr?.rallyIndex ?? rr?.rally_index ?? rr?.rally ?? rr?.index ?? null;

          const n = typeof v === "number" ? v : Number(v);
          return Number.isFinite(n) ? n : null;
        })
        .filter((x: any): x is number => x != null);

      if (out.length) return out;
    }

    return [];
  }

  function dedupeKey_old(it: InfluenceNavItem) {
    return [it.team, it.bucketId, it.featureKey ?? "feature", it.impact].join(
      "|",
    );
  }

  function dedupeKey(it: InfluenceNavItem) {
    return [it.team, it.source, it.featureKey ?? "feature"].join("|");
  }

  function rankItem(it: InfluenceNavItem): number {
    let score = 0;

    // Prefer triage over influence
    if (it.influencePick) score += 100;

    // Prefer items with rally indices
    score += Math.min(it.rallyIndicesToReview?.length ?? 0, 20);

    // Prefer larger magnitude delta
    score += Math.abs(it.delta ?? 0) * 10;

    // Slight preference for SERVE (usually more actionable)
    if (it.source === "serve") score += 2;

    return score;
  }

  const navItems: InfluenceNavItem[] = useMemo(() => {
    const rep = influenceReport;
    const tri = rep?.videoTriageByTeam;
    if (!tri) return [];

    const out: InfluenceNavItem[] = [];

    type SideoutMode =
      | "onServePoints"
      | "onReceiveSideout"
      | "onReceiveLostGamePoint";

    function impactForMode(
      mode: SideoutMode,
      delta: number,
    ): "HELPED" | "HURT" {
      // if (mode === "onServePoints") return delta >= 0 ? "HELPED" : "HURT";
      // if (mode === "onReceiveSideout") return delta >= 0 ? "HELPED" : "HURT";
      // onReceiveLostGamePoint: higher = worse (more lost points)
      return delta >= 0 ? "HELPED" : "HURT"; // : "HELPED";
    }

    function contextForMode(mode: SideoutMode): "SERVE" | "RECEIVE" {
      return mode === "onServePoints" ? "SERVE" : "RECEIVE";
    }

    function bucketFor(
      mode: SideoutMode,
      impact: "HELPED" | "HURT",
      outcome: "GOOD" | "BAD",
    ): BucketId {
      const ctx = contextForMode(mode);

      // GOOD/BAD is already “goodOutcome” consistent because backend normalized it.
      if (ctx === "SERVE") {
        // GOOD = you scored on serve (won rally)
        if (impact === "HELPED")
          return outcome === "GOOD" ? "help_won" : "help_lost";
        return outcome === "GOOD" ? "hurt_survived" : "hurt_lost";
      }

      // RECEIVE:
      // - onReceiveSideout: GOOD = you got sideout (won rally), BAD = conceded
      // - onReceiveLostGamePoint: GOOD = avoided losing point (won rally), BAD = lost point
      if (impact === "HELPED")
        return outcome === "GOOD" ? "help_won" : "help_lost";
      return outcome === "GOOD" ? "hurt_survived" : "hurt_lost";
    }

    // Creates nav items from influence rows (feature catalog), not triage picks.
    function pushInfluenceRow(
      row: any,
      meta: { team: 0 | 1; mode: SideoutMode },
    ) {
      if (!row) return;

      const BLOCK = new Set([
        "weMissedServe",
        "weMissedReturn",
        "weHitShortReturn",
      ]);
      if (BLOCK.has(row.featureKey)) return;

      const mode = meta.mode;
      // const impact = impactForMode(mode, Number(row.delta ?? 0));

      const ALWAYS_HELPED = new Set([
        // serve
        "weServedDeep",

        // terminal winners (should never be treated as “hurt”)
        "wePutawayWon",
        "opponentPutawayWon", // note: “opponentPutawayWon” is HELPED for the opponent-team’s view
      ]);

      const FORCE_IMPACT: Record<string, "HELPED" | "HURT"> = {
        weServedDeep: "HELPED",
        wePutawayWon: "HELPED",
        weServedFast: "HELPED",
        opponentPutawayWon: "HURT",

        // ✅ 3rd-shot drop popup semantics
        weThirdDropPopup: "HURT",
        weThirdDropPopupPunished: "HURT",
        opponentThirdDropPopup: "HELPED",
        opponentThirdDropPopupPunished: "HELPED",
      };

// console.error();
      let impact =
        FORCE_IMPACT[row.featureKey] ??
        impactForMode(mode, Number(row.delta ?? 0));
      // const impact =
      //   mode === "onServePoints" && row.featureKey === "weServedDeep"
      //     ? "HELPED"
      //     : impactForMode(mode, Number(row.delta ?? 0));

      // let impact =
      //   (mode === "onServePoints" && row.featureKey === "weServedDeep") ||
      //   ALWAYS_HELPED.has(row.featureKey)
      //     ? "HELPED"
      //     : impactForMode(mode, Number(row.delta ?? 0));

      // if (ALWAYS_HELPED.has(row.featureKey)) {
      //   impact = "HELPED";
      //}
      const ctx = contextForMode(mode);

      const goodIdxs = uniqSorted(
        Array.isArray(row.trueGoodOutcomeRallyIndices)
          ? row.trueGoodOutcomeRallyIndices
          : [],
      );
      const badIdxs = uniqSorted(
        Array.isArray(row.trueBadOutcomeRallyIndices)
          ? row.trueBadOutcomeRallyIndices
          : [],
      );

      const wantOutcome: "GOOD" | "BAD" = impact === "HURT" ? "BAD" : "GOOD";
      const rallyIndicesToReview = wantOutcome === "GOOD" ? goodIdxs : badIdxs;
      if (!rallyIndicesToReview.length) return;

      // bucketId should match that narrative too
      const bucketId = bucketFor(mode, impact, wantOutcome);

      // We create two items per feature per mode:
      // - feature present + good outcome
      // - feature present + bad outcome
      const mk = (outcome: "GOOD" | "BAD", rallyIndicesToReview: number[]) => {
        if (!rallyIndicesToReview.length) return;

        const bucketId = bucketFor(mode, impact, outcome);
        const source: "serve" | "receive" =
          ctx === "SERVE" ? "serve" : "receive";

        const sig = `${rallyIndicesToReview.length}:${
          rallyIndicesToReview[0]
        }:${rallyIndicesToReview[rallyIndicesToReview.length - 1]}`;
        out.push({
          id: `INF_${meta.team}_${mode}_${row.featureKey}_${impact}_${outcome}_${sig}`,
          bucketId,
          source,
          team: meta.team,
          influencePick: null, // ✅ leave null; UI can handle as "influence nav item"
          context: ctx,
          impact,
          featureKey: row.featureKey,
          featureLabel: row.featureLabel || row.featureKey || "feature",
          layman: row.featureLayman || row.featureLayman || "",
          rallyIndicesToReview,
          delta: Number(row.delta ?? 0),
          importance: Number(row.importance ?? 0), // influence rows don’t have this; ok if 0
          supportNote: `nTrue=${row.nTrue ?? "?"}, nFalse=${row.nFalse ?? "?"}`,
          isSpecial: false,
          specialKey: undefined,
          // Optional: add a flag for rendering
          kind: "influence" as const,
          outcome,
          mode,
          rateTrue: row.rateTrue,
          rateFalse: row.rateFalse,
        } as any);
      };

      mk("GOOD", goodIdxs);
      mk("BAD", badIdxs);
    }

    const pushPick = (
      p: InfluencePick | undefined | null,
      meta: { bucketId: BucketId; source: "serve" | "receive" },
      opts?: { isSpecial?: boolean; specialKey?: InfluenceSpecialKey },
    ) => {
      if (!p) return;

      const rallyIndicesToReview = extractRallyIndicesFromPick(p);

      // ✅ For non-specials, still require indices.
      // ✅ For specials, keep it even if indices are missing (so you can SEE it and debug).
      if (!rallyIndicesToReview.length && !opts?.isSpecial) return;

      const specialKey = opts?.isSpecial
        ? (opts?.specialKey ?? "unknownSpecial")
        : "false";

      // const sig = rallyIndicesToReview.length
      //   ? `${rallyIndicesToReview.length}:${rallyIndicesToReview[0]}:
      //   ${specialKey}
      //   :${
      //       rallyIndicesToReview[rallyIndicesToReview.length - 1]
      //     }`
      //   : "0";

      const sig = rallyIndicesToReview.length
        ? `${rallyIndicesToReview.length}:${
            rallyIndicesToReview[0]
          }:${specialKey}:${
            rallyIndicesToReview[rallyIndicesToReview.length - 1]
          }`
        : `0:${specialKey}`;

      const contextFromSource = meta.source === "serve" ? "SERVE" : "RECEIVE";

      out.push({
        id: `${p.team}_${meta.source}_${meta.bucketId}_${p.context}_${
          p.impact
        }_${p.mode ?? "mode"}_${p.featureKey ?? "feature"}_${
          p.scenario ?? "scenario"
        }_${sig}`,
        // id: `${p.team}_${meta.source}_${meta.bucketId}_${p.context}_${
        //   p.impact
        // }_${p.mode ?? "mode"}_${p.featureKey ?? "feature"}_${
        //   p.scenario ?? "scenario"
        // }`,
        bucketId: meta.bucketId,
        source: meta.source,
        team: p.team,
        influencePick: p,
        context: contextFromSource, //p.context,
        impact: p.impact,
        featureKey: p.featureKey,
        featureLabel: p.featureLabel || p.featureKey || "feature",
        layman: p.layman,
        rallyIndicesToReview,
        delta: p.delta,
        importance: p.importance,
        supportNote: p.supportNote,
        isSpecial: opts?.isSpecial ?? false,
        specialKey: opts?.specialKey,
      });
    };

    const walkTeam = (t: TeamVideoTriage) => {
      console.log(
        "TEAM",
        t.team,
        "receive.specials keys",
        Object.keys((t.receive.specials as any) ?? {}),
      );
      // console.log(
      //   "TEAM",
      //   t.team,
      //   "serve.specials keys",
      //   Object.keys((t.serve.specials as any) ?? {})
      // );

      console.log("t.receive.specials", t.receive.specials);

      // Serve buckets
      for (const p of t.serve.hurtLost)
        pushPick(p, { source: "serve", bucketId: "hurt_lost" });
      for (const p of t.serve.hurtSurvived)
        pushPick(p, { source: "serve", bucketId: "hurt_survived" });
      for (const p of t.serve.helpedWon)
        pushPick(p, { source: "serve", bucketId: "help_won" });
      for (const p of t.serve.helpedLost)
        pushPick(p, { source: "serve", bucketId: "help_lost" });
      // // ✅ SPECIALS
      // pushPick(t.serve.specials?.missedServe ?? null, {
      //   isSpecial: true,
      //   specialKey: "missedServe",
      // });
      // Receive buckets
      for (const p of t.receive.hurtLost)
        pushPick(p, { source: "receive", bucketId: "hurt_lost" });
      for (const p of t.receive.hurtSurvived)
        pushPick(p, { source: "receive", bucketId: "hurt_survived" });
      for (const p of t.receive.helpedWon)
        pushPick(p, { source: "receive", bucketId: "help_won" });
      for (const p of t.receive.helpedLost)
        pushPick(p, { source: "receive", bucketId: "help_lost" });

      // ✅ SPECIALS
      // ✅ SPECIALS (robust to snake_case and misplaced nesting)
      const recvSpecials: any =
        t.receive?.specials ?? (t.receive as any)?.specials ?? null;
      const serveSpecials: any =
        t.serve?.specials ?? (t.serve as any)?.specials ?? null;

      const missedServePick =
        serveSpecials?.missedServe ??
        serveSpecials?.missed_serve ??
        recvSpecials?.missedServe ?? // fallback if server placed it here
        recvSpecials?.missed_serve ??
        null;

      const missedReturnPick =
        recvSpecials?.missedReturn ??
        recvSpecials?.missed_return ??
        serveSpecials?.missedReturn ?? // fallback if server placed it here
        serveSpecials?.missed_return ??
        null;

      const shortReturnPick =
        recvSpecials?.shortReturn ??
        recvSpecials?.short_return ??
        serveSpecials?.shortReturn ??
        serveSpecials?.short_return ??
        null;

      pushPick(
        missedServePick,
        {
          source: "serve",
          bucketId: "hurt_lost",
        },
        { isSpecial: true, specialKey: "missedServe" },
      );

      pushPick(
        missedReturnPick,
        {
          source: "receive",
          bucketId: "hurt_lost",
        },
        {
          isSpecial: true,
          specialKey: "missedReturn",
        },
      );
      pushPick(
        shortReturnPick,
        {
          source: "receive",
          bucketId: "hurt_lost",
        },
        {
          isSpecial: true,
          specialKey: "shortReturn",
        },
      );

      // pushPick(missedReturnPick, {
      //   isSpecial: true,
      //   specialKey: "missedReturn",
      // });

      // console.log("serve.specials", t.team, t.serve.specials);
      // console.log("receive.specials", t.team, t.receive.specials);
    };

    walkTeam(tri.team0);
    walkTeam(tri.team1);

    const repAny: any = influenceReport;

    function walkInfluenceTeam(teamNum: 0 | 1) {
      const t = teamNum === 0 ? repAny?.team0 : repAny?.team1;
      const infl = t?.influence;
      if (!infl) return;

      for (const row of infl.onServePoints ?? []) {
        pushInfluenceRow(row, { team: teamNum, mode: "onServePoints" });
      }
      for (const row of infl.onReceiveSideout ?? []) {
        pushInfluenceRow(row, { team: teamNum, mode: "onReceiveSideout" });
      }
      for (const row of infl.onReceiveLostGamePoint ?? []) {
        pushInfluenceRow(row, {
          team: teamNum,
          mode: "onReceiveLostGamePoint",
        });
      }
    }

    walkInfluenceTeam(0);
    walkInfluenceTeam(1);

    const byKey = new Map<string, InfluenceNavItem>();

    for (const it of out) {
      const k = dedupeKey(it);
      const prev = byKey.get(k);
      if (!prev) {
        byKey.set(k, it);
      } else {
        if (rankItem(it) > rankItem(prev)) {
          byKey.set(k, it);
        }
      }
    }

    const out_2 = Array.from(byKey.values()).sort(
      (a, b) => -a.rallyIndicesToReview.length - b.rallyIndicesToReview.length,
    );

    return out_2;

    // return out;
  }, [influenceReport]);

  const rallyWinnerTeamByRallyIndex = useMemo(() => {
    const m: Record<number, 0 | 1 | null> = {};
    for (const r of rows ?? []) m[r.rallyIndex] = (r.winnerTeam ?? null) as any;
    return m;
  }, [rows]);

  const pointWinnerTeamByRallyIndex = useMemo(() => {
    const m: Record<number, 0 | 1 | undefined> = {};

    for (const r of rows as any[]) {
      const st = r.servingTeam;
      const wt = r.winnerTeam;

      // Only serving team can be awarded a point.
      // If receiver wins => sideout => NO point awarded => undefined (not "unknown").
      const awardedTeam: 0 | 1 | undefined =
        st === 0 || st === 1
          ? wt === 0 || wt === 1
            ? st === wt
              ? (wt as 0 | 1)
              : undefined
            : undefined
          : undefined;

      m[r.rallyIndex] = awardedTeam;
    }

    return m;
  }, [rows]);

  // const pointWinnerTeamByRallyIndex_oldold = useMemo(() => {
  //   const m: Record<number, 0 | 1 | null> = {};

  //   // const toTeam = (v: any): 0 | 1 | null => {
  //   //   if (v === 0 || v === 1) return v;
  //   //   if (typeof v === "string") {
  //   //     const s = v.toLowerCase();
  //   //     // adjust these if your canonical values differ
  //   //     if (s === "near") return 0;
  //   //     if (s === "far") return 1;
  //   //   }
  //   //   return null;
  //   // };

  //   for (const r of rows as any[]) {
  //     // Prefer numeric winnerTeam, else derive from a string field if present.

  //     // ✅ Authoritative rule: only the serving team can be awarded a point.
  //     // If the receiver wins, it's a sideout => NO point awarded.
  //     const st = r.servingTeam ?? null;
  //     const wt = r.winnerTeam ?? null;

  //     const pointTeam: Team01 | null =
  //       st != null && wt != null && st === wt ? (wt as Team01) : null;

  //     // Optional: if backend is sending pointAwardedTeam, you can sanity-check it:
  //     // const backendPoint = (r.pointAwardedTeam ?? null) as Team01 | null;
  //     // if (backendPoint != null && backendPoint !== pointTeam) {
  //     //   console.warn("Inconsistent pointAwardedTeam; overriding", {
  //     //     rallyIndex: r.rallyIndex,
  //     //     servingTeam: st,
  //     //     winnerTeam: wt,
  //     //     backendPointAwardedTeam: backendPoint,
  //     //     computedPointAwardedTeam: pointTeam,
  //     //   });
  //     // }

  //     m[r.rallyIndex] = pointTeam;
  //     // const derived =
  //     //   toTeam(r.winnerTeam) ??
  //     //   toTeam(r.rallyWinner) ??      // <-- common case
  //     //   toTeam(r.pointWinner) ??      // <-- if you have it
  //     //   null;

  //     // m[r.rallyIndex] = derived;
  //   }

  //   return m;
  // }, [rows]);

  // const pointWinnerTeamByRallyIndex_old = useMemo(() => {
  //   const m: Record<number, 0 | 1 | null> = {};
  //   for (const r of rows ?? []) {
  //     // console.error({ r });

  //     let pointTeam: Team01 | null = r.pointAwardedTeam ?? null;

  //     // Otherwise infer: only the server can score.
  //     if (pointTeam == null) {
  //       const st = r.servingTeam;
  //       const wt = r.winnerTeam;
  //       if (st != null && wt != null && st === wt) pointTeam = wt;
  //     }

  //     m[r.rallyIndex] = pointTeam;

  //     // // Prefer explicit point winner, fallback to rally winner if server didn’t provide it.
  //     // const pw = (r.pointAwardedTeam ?? null) as any;
  //     // m[r.rallyIndex] = pw != null ? pw : ((r.winnerTeam ?? null) as any);
  //   }
  //   return m;
  // }, [rows]);

  const pointKeyByRallyIndex = useMemo(() => {
    const m: Record<number, string | number | null> = {};
    for (const r of rows ?? []) {
      m[r.rallyIndex] = (r.pointKey ?? null) as any;
    }
    return m;
  }, [rows]);

  // Load once when opened (or when packageId changes)
  React.useEffect(() => {
    if (!props.open) return;

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        /**
         * ASSUMPTION: you have (or can easily add) a report endpoint like:
         *   GET {baseUrl}/api/packages/{packageId}/kitchen_arrival/report
         * which returns:
         *   { rows: KitchenArrivalRow[], turnsByClipId: Record<string, AiTurn[]> }
         *
         * If your endpoint is named differently, just change this URL.
         */
        // const url = baseUrl
        //   ? `${baseUrl}/api/packages/${encodeURIComponent(
        //       props.packageId
        //     )}/kitchen_arrival/report`
        //   : `/api/packages/${encodeURIComponent(
        //       props.packageId
        //     )}/kitchen_arrival/report`;

        // const data = await fetchJson<{
        //   rows: KitchenArrivalRow[];
        //   turnsByClipId?: Record<string, AiTurn[]>;
        // }>(url);

        const data1 = await fetch(`/api/kitchen-arrival-report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ packageId: props.packageId }),
        });
        if (cancelled) return;

        const data = (await data1.json()) as {
          rows: KitchenArrivalRow[];
          // turnsByClipId?: Record<string, AiTurn[]>;
          turnsByKey?: Record<string, AiTurn[]>;
          sideOutGameInfluenceReport: SideoutGameInfluenceReport;
        };

// console.error({ data });

        setRows(data.rows ?? []);
        setTurnsByKey((data.turnsByKey ?? {}) as Record<string, AiTurn[]>);
        setInfluenceReport(data.sideOutGameInfluenceReport ?? null);
      } catch (e: any) {
        if (cancelled) return;
        setLoadError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [props.open, props.packageId, baseUrl]);

  // const { nearRows, farRows, unknownRows } = useMemo(() => {
  //   const near: KitchenArrivalRow[] = [];
  //   const far: KitchenArrivalRow[] = [];
  //   const unk: KitchenArrivalRow[] = [];

  //   for (const r of rows) {
  //     const side = (r.winnerSide ?? "unknown") as Side;
  //     if (side === "near") near.push(r);
  //     else if (side === "far") far.push(r);
  //     else unk.push(r);
  //   }

  //   const sortFn = (a: KitchenArrivalRow, b: KitchenArrivalRow) =>
  //     a.rallyIndex !== b.rallyIndex
  //       ? a.rallyIndex - b.rallyIndex
  //       : a.segmentStartMs - b.segmentStartMs;

  //   near.sort(sortFn);
  //   far.sort(sortFn);
  //   unk.sort(sortFn);

  //   return { nearRows: near, farRows: far, unknownRows: unk };
  // }, [rows]);

  const filteredRows = useMemo(() => {
    return (rows ?? []).filter((r) => {
      const wSide = (r.winnerSide ?? "unknown") as Side;
      const sSide = (r.servingSide ?? "unknown") as Side;

      const got = triFromNullableBool(r.servingArrivalSuccess);
      const opp = triFromNullableBool(r.servingHadOpportunity);
      const likelyIncorrect = triFromNullableBool(
        typeof r.likely_correct_score === "undefined"
          ? null
          : !r.likely_correct_score,
      );

      const key = rallyKey(r);
      const hasAiTurns = (turnsByKey[key]?.length ?? 0) > 0;
      const hasAi: HasAi = hasAiTurns ? "has_ai" : "no_ai";

      const segType = segTypeFromReason(r.segmentReason);

      if (!filters.winnerSides.has(wSide)) return false;
      if (!filters.servingSides.has(sSide)) return false;
      if (!filters.gotToKitchen.has(got)) return false;
      if (!filters.likelyIncorrectScore.has(likelyIncorrect)) return false;
      if (!filters.hadOpportunity.has(opp)) return false;
      if (!filters.hasAi.has(hasAi)) return false;
      if (!filters.segmentType.has(segType)) return false;

      if (activeInfluenceRallyIndexSet) {
        if (!activeInfluenceRallyIndexSet.has(r.rallyIndex)) return false;
      }

      const n = typeof r.numShots === "number" ? r.numShots : null;
      if (n == null) return false; // or treat unknown as pass-through if you prefer
      if (n < shotRange[0] || n > shotRange[1]) return false;

      return true;
    });
  }, [rows, turnsByKey, filters, shotRange, activeInfluenceRallyIndexSet]);

  React.useEffect(() => {
    const vals = (rows ?? [])
      .map((r) => (typeof r.numShots === "number" ? r.numShots : null))
      .filter((x): x is number => typeof x === "number");

    if (!vals.length) return;

    const min = Math.min(...vals);
    const max = Math.max(...vals);

    setShotRange((cur) => {
      // If the user hasn't interacted yet, default to FULL RANGE.
      if (!shotRangeTouchedRef.current) return [min, max];

      // If they *have* interacted, just keep it clamped.
      return [clamp(cur[0], min, max), clamp(cur[1], min, max)];
    });
  }, [rows]);

  const { nearRows, farRows, unknownRows } = useMemo(() => {
    const near: KitchenArrivalRow[] = [];
    const far: KitchenArrivalRow[] = [];
    const unk: KitchenArrivalRow[] = [];

    for (const r of filteredRows) {
      const side = (r.winnerSide ?? "unknown") as Side;
      if (side === "near") near.push(r);
      else if (side === "far") far.push(r);
      else unk.push(r);
    }

    const sortFn = (a: KitchenArrivalRow, b: KitchenArrivalRow) =>
      a.rallyIndex !== b.rallyIndex
        ? a.rallyIndex - b.rallyIndex
        : a.segmentStartMs - b.segmentStartMs;

    near.sort(sortFn);
    far.sort(sortFn);
    unk.sort(sortFn);

    return { nearRows: near, farRows: far, unknownRows: unk };
  }, [filteredRows]);

  const handleAskAi = async (args: {
    row: KitchenArrivalRow;
    question: string;
    locator?: string; // we'll map it below
  }) => {
    const url = baseUrl ? `${baseUrl}/api/ai-ask-rally` : `/api/ai-ask-rally`;

    // optional locator mapping: "near_left" -> {side:"near", lr:"left"}
    const loc = !args.locator
      ? null
      : args.locator === "near_left"
        ? { side: "near", lr: "left" }
        : args.locator === "near_right"
          ? { side: "near", lr: "right" }
          : args.locator === "far_left"
            ? { side: "far", lr: "left" }
            : args.locator === "far_right"
              ? { side: "far", lr: "right" }
              : args.locator; // fallback: keep string if you ever pass a custom one

    const body = {
      packageId: props.packageId,
      rallyIndex: args.row.rallyIndex,
      question: args.question,
      segmentStartMs: args.row.segmentStartMs,
      segmentEndMs: args.row.segmentEndMs,
      locator: loc,
      // optional future tuning:
      // framePolicy: { everyN: 3, maxFramesSafety: 80 },
    };

    const resp = await postJson<any>(url, body);

    const key = rallyKey(args.row);

    const nextTurns: AiTurn[] = Array.isArray(resp?.turns)
      ? resp.turns
      : resp?.turn
        ? [...(turnsByKey[key] ?? []), resp.turn]
        : (turnsByKey[key] ?? []);

    setTurnsForKey(key, nextTurns);
  };

  const handleDeleteTurn = async (row: KitchenArrivalRow, turnId: string) => {
    // Placeholder endpoint you’ll implement later
    const url = baseUrl
      ? `${baseUrl}/api/ai-delete-rally-turn`
      : `/api/ai-delete-rally-turn`;

    await postJson(url, {
      packageId: props.packageId,
      rallyIndex: row.rallyIndex,
      turnId,
    });

    const key = rallyKey(row);
    setTurnsForKey(
      key,
      (turnsByKey[key] ?? []).filter((t) => t.id !== turnId),
    );
  };

  async function postDownload(url: string, body: any, filenameHint?: string) {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!r.ok) {
      const txt = await r.text().catch(() => "");
      throw new Error(`Export failed (${r.status}): ${txt}`);
    }

    const blob = await r.blob();

    // Try to pull filename from Content-Disposition
    const cd = r.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="([^"]+)"/i);
    const filename = match?.[1] || filenameHint || "export.html";

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  }

  const handleExport = async (
    row: KitchenArrivalRow,
    activeInfluence: InfluenceNavItem | undefined | null,
  ) => {
    // choose ONE endpoint style; this keeps your existing route shape
    const url = baseUrl
      ? `${baseUrl}/api/export-rally-ai`
      : `/api/export-rally-ai`;

    const ap = (activeInfluence ?? null) as {
      context?: string;
      featureLabel?: string;
      id?: number | null;
    } | null;

    const chipsInfo = buildRallySummaryChips(row);

    const activePickedLabel =
      ap?.context && ap?.featureLabel
        ? `Picked: ${ap.context}: ${ap.featureLabel}${
            typeof ap.id === "number" ? ` (${ap.id})` : ""
          }`
        : null;

    await postDownload(
      url,
      {
        packageId: props.packageId,
        rallyIndex: row.rallyIndex,
        segmentStartMs: row.segmentStartMs,
        segmentEndMs: row.segmentEndMs,
        exportChips: chipsInfo,
        activePick: ap,
        exportShotOverlays: row.shotNumberOverlays ?? null,
      },
      `r${row.rallyIndex}_seg_${row.segmentStartMs}_${row.segmentEndMs}.html`,
    );

    // await postJson(url, {
    //   packageId: props.packageId, // optional if already implied by URL
    //   rallyIndex: row.rallyIndex,
    //   segmentStartMs: row.segmentStartMs,
    //   segmentEndMs: row.segmentEndMs,
    // });
  };

  const stickyRef = React.useRef<HTMLDivElement | null>(null);

  const histogramComponent = useMemo(
    () => (
      <ShotsHistogramFilter
        rows={rows}
        range={shotRange}
        onChangeRange={setShotRangeFromHistogram}
        containerRef={stickyRef}
        title="Shots/Rally"
      />
    ),
    [rows, shotRange, setShotRangeFromHistogram, stickyRef],
  );

  const subFilterComponent = useMemo(() => {
    const chipSx = (on: boolean) => ({
      height: 20, // <- key
      borderRadius: 999,
      fontSize: 11,
      fontWeight: on ? 700 : 500,

      // reduce inner padding
      "& .MuiChip-label": {
        px: 0.75,
        py: 0,
        lineHeight: 1,
      },

      // optional: tighten icon sizes if any
      "& .MuiChip-icon, & .MuiChip-deleteIcon": {
        fontSize: 16,
      },
    });

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          flexWrap: "wrap",
          pr: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.5,
            flexWrap: "wrap",
            flex: "1 1 700px",
            minWidth: 520,
          }}
        >
          <Group label="Got to Kitchen">
            <Chip
              size="small"
              label="Yes"
              color={filters.gotToKitchen.has("yes") ? "primary" : "default"}
              variant={filters.gotToKitchen.has("yes") ? "filled" : "outlined"}
              sx={chipSx(filters.gotToKitchen.has("yes"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  gotToKitchen: toggleInSet(f.gotToKitchen, "yes"),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  gotToKitchen: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "yes",
                  ),
                }));
              }}
            />
            <Chip
              size="small"
              label="No"
              color={filters.gotToKitchen.has("no") ? "primary" : "default"}
              variant={filters.gotToKitchen.has("no") ? "filled" : "outlined"}
              sx={chipSx(filters.gotToKitchen.has("no"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  gotToKitchen: toggleInSet(f.gotToKitchen, "no"),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  gotToKitchen: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "no",
                  ),
                }));
              }}
            />
            <Chip
              size="small"
              label="Unknown"
              color={
                filters.gotToKitchen.has("unknown") ? "primary" : "default"
              }
              variant={
                filters.gotToKitchen.has("unknown") ? "filled" : "outlined"
              }
              sx={chipSx(filters.gotToKitchen.has("unknown"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  gotToKitchen: toggleInSet(f.gotToKitchen, "unknown"),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  gotToKitchen: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "unknown",
                  ),
                }));
              }}
            />
          </Group>

          <Group label="Kitchen Opportunity">
            <Chip
              size="small"
              label="Yes"
              color={filters.hadOpportunity.has("yes") ? "primary" : "default"}
              variant={
                filters.hadOpportunity.has("yes") ? "filled" : "outlined"
              }
              sx={chipSx(filters.hadOpportunity.has("yes"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  hadOpportunity: toggleInSet(f.hadOpportunity, "yes"),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  hadOpportunity: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "yes",
                  ),
                }));
              }}
            />
            <Chip
              size="small"
              label="No"
              color={filters.hadOpportunity.has("no") ? "primary" : "default"}
              variant={filters.hadOpportunity.has("no") ? "filled" : "outlined"}
              sx={chipSx(filters.hadOpportunity.has("no"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  hadOpportunity: toggleInSet(f.hadOpportunity, "no"),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  hadOpportunity: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "no",
                  ),
                }));
              }}
            />
          </Group>

          <Group label="Rally Winner">
            {(["near", "far", "unknown"] as Side[]).map((s) => {
              const on = filters.winnerSides.has(s);
              return (
                <Chip
                  key={s}
                  size="small"
                  label={
                    s === "near" ? "Near" : s === "far" ? "Far" : "Unknown"
                  }
                  color={on ? "primary" : "default"}
                  variant={on ? "filled" : "outlined"}
                  sx={chipSx(on)}
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      winnerSides: toggleInSet(f.winnerSides, s),
                    }))
                  }
                  onContextMenu={(e: any) => {
                    e.preventDefault();
                    setFilters((f) => ({
                      ...f,
                      winnerSides: soloInSet(
                        ["near", "far", "unknown"] as const,
                        s,
                      ),
                    }));
                  }}
                />
              );
            })}
          </Group>

          <Group label="Server">
            {(["near", "far", "unknown"] as Side[]).map((s) => {
              const on = filters.servingSides.has(s);
              return (
                <Chip
                  key={s}
                  size="small"
                  label={
                    s === "near" ? "Near" : s === "far" ? "Far" : "Unknown"
                  }
                  color={on ? "primary" : "default"}
                  variant={on ? "filled" : "outlined"}
                  sx={chipSx(on)}
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      servingSides: toggleInSet(f.servingSides, s),
                    }))
                  }
                  onContextMenu={(e: any) => {
                    e.preventDefault();
                    setFilters((f) => ({
                      ...f,
                      servingSides: soloInSet(
                        ["near", "far", "unknown"] as const,
                        s,
                      ),
                    }));
                  }}
                />
              );
            })}
          </Group>

          <Group label="AI Responses">
            <Chip
              size="small"
              label="Has"
              color={filters.hasAi.has("has_ai") ? "primary" : "default"}
              variant={filters.hasAi.has("has_ai") ? "filled" : "outlined"}
              sx={chipSx(filters.hasAi.has("has_ai"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  hasAi: toggleInSet(f.hasAi, "has_ai"),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  hasAi: soloInSet(["has_ai", "no_ai"] as const, "has_ai"),
                }));
              }}
            />
            <Chip
              size="small"
              label="None"
              color={filters.hasAi.has("no_ai") ? "primary" : "default"}
              variant={filters.hasAi.has("no_ai") ? "filled" : "outlined"}
              sx={chipSx(filters.hasAi.has("no_ai"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  hasAi: toggleInSet(f.hasAi, "no_ai"),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  hasAi: soloInSet(["has_ai", "no_ai"] as const, "no_ai"),
                }));
              }}
            />
          </Group>

          <Group label="Possible Incorrect Score">
            <Chip
              size="small"
              label="Yes"
              color={
                filters.likelyIncorrectScore.has("yes") ? "primary" : "default"
              }
              variant={
                filters.likelyIncorrectScore.has("yes") ? "filled" : "outlined"
              }
              sx={chipSx(filters.likelyIncorrectScore.has("yes"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  likelyIncorrectScore: toggleInSet(
                    f.likelyIncorrectScore,
                    "yes",
                  ),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  likelyIncorrectScore: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "yes",
                  ),
                }));
              }}
            />
            <Chip
              size="small"
              label="No"
              color={
                filters.likelyIncorrectScore.has("no") ? "primary" : "default"
              }
              variant={
                filters.likelyIncorrectScore.has("no") ? "filled" : "outlined"
              }
              sx={chipSx(filters.likelyIncorrectScore.has("no"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  likelyIncorrectScore: toggleInSet(
                    f.likelyIncorrectScore,
                    "no",
                  ),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  likelyIncorrectScore: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "no",
                  ),
                }));
              }}
            />
            <Chip
              size="small"
              label="Unknown"
              color={
                filters.likelyIncorrectScore.has("unknown")
                  ? "primary"
                  : "default"
              }
              variant={
                filters.likelyIncorrectScore.has("unknown")
                  ? "filled"
                  : "outlined"
              }
              sx={chipSx(filters.likelyIncorrectScore.has("unknown"))}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  likelyIncorrectScore: toggleInSet(
                    f.likelyIncorrectScore,
                    "unknown",
                  ),
                }))
              }
              onContextMenu={(e: any) => {
                e.preventDefault();
                setFilters((f) => ({
                  ...f,
                  likelyIncorrectScore: soloInSet(
                    ["yes", "no", "unknown"] as const,
                    "unknown",
                  ),
                }));
              }}
            />
          </Group>
        </Box>
      </Box>
    );
  }, [rows, filters]);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      PaperComponent={PaperComponent}
      PaperProps={{
        sx: {
          width: "95vw",
          height: "95vh",
          maxWidth: "95vw",
          maxHeight: "95vh",
          m: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        id="kitchen-arrival-popup-title"
        sx={{
          paddingLeft: "10px !important",
          cursor: "move",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ fontWeight: 900, fontSize: 14 }}>
              {"Rally Report: " + props.packageId}
            </Box>
          </Box>

          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowTop((v) => !v)}
          >
            {showTop ? "Hide summary and filters" : "Show summary and filters"}
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip size="small" label={`${rows.length} rallies`} />
            {/* <Chip
            size="small"
            label={`Showing ${filteredRows.length} / ${rows.length}`}
            variant="outlined"
          /> */}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ fontSize: 12 }}>
                Showing <b>{filteredRows.length}</b> / {rows.length}
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setFilters(DEFAULT_FILTERS)}
              >
                Reset filters
              </Button>
            </Box>

            {loading ? <Chip size="small" label="Loading..." /> : null}
            {loadError ? <Chip size="small" label="Load error" /> : null}
          </Box>
        </>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative", // helps sticky behave predictably
          //marginTop: "5px",
          padding: "0 !important",
          paddingLeft: "10px !important",
        }}
      >
        <Box
          ref={stickyRef}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: (theme: any) => theme.zIndex.appBar + 1,
            bgcolor: "#fff",
            pt: 1,
            pb: 1,
            mb: 1,
            overflow: "visible",
            borderBottom: "1px solid rgba(0,0,0,0.10)",
          }}
        >
          <Collapse in={showTop} timeout={200} unmountOnExit>
            {influenceReport ? (
              <InfluenceNavigatorMatrix
                histogramComponent={histogramComponent}
                subFilterComponent={subFilterComponent}
                items={navItems}
                rallyWinnerTeamByRallyIndex={rallyWinnerTeamByRallyIndex}
                pointWinnerTeamByRallyIndex={pointWinnerTeamByRallyIndex}
                pointKeyByRallyIndex={pointKeyByRallyIndex}
                active={activeInfluence}
                onPick={(it) => {
                  setActiveInfluence(it);
                  setActiveReviewIdx(0);
                  //setTimeout(() => scrollToReviewIndex(0), 0);
                }}
                onClear={() => {
                  setActiveInfluence(null);
                  setActiveReviewIdx(0);
                }}
              />
            ) : null}

            {loadError ? (
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.2)",
                }}
              >
                <Box sx={{ fontWeight: 900, mb: 0.5 }}>Failed to load</Box>
                <Box sx={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
                  {loadError}
                </Box>
              </Box>
            ) : null}
          </Collapse>

          <SelectionSummarySticky
            stickyTopPx={0}
            activePick={activeInfluence}
            onClearPick={() => setActiveInfluence(null)} // or whatever clears
            filters={filters}
            setFilters={setFilters}
            shotRange={shotRange}
            fullShotRange={fullShotRange} // ✅ add
            onClearShotRange={resetShotRangeToFull} // ✅ real reset
            onResetAll={() => {
              setActiveInfluence(null);
              setFilters(DEFAULT_FILTERS);
              resetShotRangeToFull();
            }}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <WinnerSideSection
              title="Near-side winners"
              side="near"
              rows={nearRows}
              packageId={props.packageId}
              turnsByKey={turnsByKey}
              setTurnsForKey={setTurnsForKey}
              onAskAi={handleAskAi}
              onExport={handleExport}
              activeInfluence={activeInfluence}
            />

            <WinnerSideSection
              title="Far-side winners"
              side="far"
              rows={farRows}
              packageId={props.packageId}
              turnsByKey={turnsByKey}
              setTurnsForKey={setTurnsForKey}
              onAskAi={handleAskAi}
              onExport={handleExport}
              activeInfluence={activeInfluence}
            />

            <WinnerSideSection
              title="Unknown winners"
              side="unknown"
              rows={unknownRows}
              packageId={props.packageId}
              turnsByKey={turnsByKey}
              setTurnsForKey={setTurnsForKey}
              onAskAi={handleAskAi}
              onExport={handleExport}
              activeInfluence={activeInfluence}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button
          onClick={props.onClose}
          variant="contained"
          sx={{ textTransform: "none" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  // -------------------------------
  // WinnerSideSection (inner)
  // -------------------------------
  function WinnerSideSection({
    title,
    side,
    rows,
    packageId,
    turnsByKey,
    setTurnsForKey,
    onAskAi,
    onExport,
    activeInfluence,
    compactVideoHeight = 260,
  }: {
    title: string;
    side: Side;
    activeInfluence: InfluenceNavItem | undefined | null;
    rows: KitchenArrivalRow[];
    packageId: string;

    turnsByKey: Record<string, AiTurn[]>;
    setTurnsForKey: (key: string, turns: AiTurn[]) => void;

    onAskAi: (args: {
      row: KitchenArrivalRow;
      question: string;
      locator?: string;
    }) => Promise<void> | void;
    onExport: (
      row: KitchenArrivalRow,
      activeInfluence: InfluenceNavItem | undefined | null,
    ) => Promise<void> | void;

    compactVideoHeight?: number;
  }) {
    const filtersSummary = summarizeFilters(filters); // from parent scope

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <WinnerSideLabel side={side} />
          <Box sx={{ fontWeight: 800, fontSize: 12, opacity: 0.7 }}>
            {title}
          </Box>
          <Box sx={{ fontSize: 12, opacity: 0.6 }}>({rows.length})</Box>
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* <Chip
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: 12, opacity: 0.85 }}
          label={"blah" + filtersSummary}
        /> */}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {rows.map((r) => {
            const key = rallyKey(r);
            return (
              <KitchenArrivalClipCard
                packageId={packageId}
                key={key}
                r={r}
                turns={turnsByKey[key] ?? []}
                onAskAi={(question, locator) =>
                  void onAskAi({ row: r, question, locator })
                }
                onDeleteTurn={(turnId) => void handleDeleteTurn(r, turnId)}
                onExport={() => onExport(r, activeInfluence)}
                compactVideoHeight={compactVideoHeight}
              />
            );
          })}
        </Box>
      </Box>
    );
  }
}

function tryParseJsonish(x: any): any | null {
  if (!x) return null;
  if (typeof x === "object") return x;

  if (typeof x === "string") {
    const s = x.trim();
    if (!s) return null;
    // quick guard for likely JSON
    if (!(s.startsWith("{") || s.startsWith("["))) return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }

  return null;
}

function extractRallyOverviewFields_prev(parsed: any): {
  answerToQuestion?: string;
  keyTurningPoint?: string;
  overallConfidence?: number;
} {
  const answerToQuestion =
    typeof parsed?.answerToQuestion === "string"
      ? parsed.answerToQuestion
      : undefined;

  const keyTurningPoint =
    typeof parsed?.rallyOverview?.keyTurningPoint === "string"
      ? parsed.rallyOverview.keyTurningPoint
      : undefined;

  const overallConfidence =
    typeof parsed?.overallConfidence === "number"
      ? parsed.overallConfidence
      : undefined;

  return { answerToQuestion, keyTurningPoint, overallConfidence };
}

function extractRallyOverviewFields(parsed: any): {
  answerToQuestion?: string;
  keyTurningPoint?: string;
  overallConfidence?: number;
  rallyOverview?: any;
} {
  const answerToQuestion =
    typeof parsed?.answerToQuestion === "string"
      ? parsed.answerToQuestion
      : undefined;

  const keyTurningPoint =
    typeof parsed?.rallyOverview?.keyTurningPoint === "string"
      ? parsed.rallyOverview.keyTurningPoint
      : undefined;

  const overallConfidence =
    typeof parsed?.overallConfidence === "number"
      ? parsed.overallConfidence
      : undefined;

  const rallyOverview =
    parsed?.rallyOverview && typeof parsed.rallyOverview === "object"
      ? parsed.rallyOverview
      : undefined;

  return {
    answerToQuestion,
    keyTurningPoint,
    overallConfidence,
    rallyOverview,
  };
}

// -------------------------------
// Clip card
// -------------------------------
function KitchenArrivalClipCard({
  r,
  packageId,
  turns,
  // setTurns,
  onAskAi,
  onExport,
  onDeleteTurn,
  compactVideoHeight,
}: {
  r: KitchenArrivalRow;
  packageId: string;
  turns: AiTurn[];
  // setTurns: (turns: AiTurn[]) => void;

  onAskAi: (question: string, locator?: string) => Promise<void> | void;

  onExport: (row: KitchenArrivalRow) => Promise<void> | void;
  onDeleteTurn: (turnId: string) => void;

  compactVideoHeight: number;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [firefightClips, setFirefightClips] = React.useState<FirefightClip[]>(
    [],
  );

  const mphHighlightValue = 30;
  useAutoPauseWhenOffscreen({
    videoRef,
    visibleThreshold: 0.35, // tweak: 0.2 = more eager to play, 0.6 = more strict
    autoResume: true,
  });

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // ensure desired defaults
    v.loop = true;
    v.muted = true; // autoplay usually requires muted
    v.playsInline = true;

    // try autoplay (may be blocked)
    const tryPlay = async () => {
      try {
        await v.play();
      } catch {
        // ignore; user interaction will be needed
      }
    };

    void tryPlay();
  }, [r.videoUrl]);

  const [question, setQuestion] = useState("");
  const [locator, setLocator] = useState<string>("");

  // const helperQuestions = useMemo(
  //   () => [
  //     "What did this player do right/wrong?",
  //     "How can they improve next rep?",
  //     "What was the main pattern in this rally?",
  //     "addOverlays for near right positioning",
  //   ],
  //   []
  // );

  function buildChatGptUrl() {
    // r: {
    // rallyIndex: number;
    // segStartMs: number;
    // segEndMs: number;
    // }
    // const prompt = [
    //   "Pickleball rally analysis - please answer the following questions about the attached mp4 for a pickleball clip: ",
    //   // "Instructions:",
    //   // "- After you download the MP4, upload it here in ChatGPT.",
    //   // "- Then ask anything you want: key turning points, best/worst shots, 2 drills, etc.",
    //   //`Rally: r${r.rallyIndex} seg ${r.segStartMs}-${r.segEndMs}ms`,
    // ].join("\n");

    return `https://chat.openai.com/?q=${encodeURIComponent("Pickleball rally analysis - please answer the following questions about the attached mp4 for a pickleball clip: ")}`;
  }

  const mp4DownloadUrl =
    `/api/rally-segment-video` +
    `?packageId=${encodeURIComponent(packageId)}` +
    `&rallyIndex=${r.rallyIndex}` +
    `&startMs=${encodeURIComponent(String(r.segmentStartMs))}` +
    `&endMs=${encodeURIComponent(String(r.segmentEndMs))}` +
    `&download=1`;

  async function downloadMp4Direct(args: {
    packageId: string;
    rallyIndex: number;
    startMs: number;
    endMs: number;
  }) {
    const { packageId, rallyIndex, startMs, endMs } = args;

    const url =
      `/api/rally-segment-video` +
      `?packageId=${encodeURIComponent(packageId)}` +
      `&rallyIndex=${rallyIndex}` +
      `&startMs=${encodeURIComponent(String(startMs))}` +
      `&endMs=${encodeURIComponent(String(endMs))}`;

    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`mp4 fetch failed: ${resp.status}`);

    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);

    const fn = `${packageId}_r${rallyIndex}_seg_${startMs}_${endMs}.mp4`;
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = fn;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
  }

  async function downloadRallyMp4(args: {
    packageId: string;
    rallyIndex: number;
    startMs: number;
    endMs: number;
  }) {
    const { packageId, rallyIndex, startMs, endMs } = args;

    const url =
      `/api/rally-segment-video` +
      `?packageId=${encodeURIComponent(packageId)}` +
      `&rallyIndex=${encodeURIComponent(String(rallyIndex))}` +
      `&startMs=${encodeURIComponent(String(startMs))}` +
      `&endMs=${encodeURIComponent(String(endMs))}` +
      `&download=1`;

    const resp = await fetch(url, { method: "GET" });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(`MP4 fetch failed (${resp.status}): ${txt}`);
    }

    const blob = await resp.blob();
    if (!blob || blob.size === 0) throw new Error("MP4 blob was empty");

    const filename = `${packageId}_r${rallyIndex}_seg_${startMs}_${endMs}.mp4`;

    const objUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
    }
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.12)",
        boxShadow: 1,
        p: 1.25,
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ fontWeight: 900, fontSize: 12 }}>r{r.rallyIndex}</Box>
          <Box sx={{ fontSize: 12, opacity: 0.7 }}>
            seg {Math.round(r.segmentStartMs)}–{Math.round(r.segmentEndMs)}ms •{" "}
            {r.segmentReason}
          </Box>

          {/* {r.winnerTeam ? (
            <Chip size="small" label={r.winnerTeam} sx={{ height: 20 }} />
          ) : null} */}

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              // match chips visually
              height: 24, // MUI small chip is ~24px tall
              ml: 0.75,
              verticalAlign: "middle",
              transform: "translateY(4px)",
            }}
          >
            <Tooltip title="Download MP4 for this rally">
              <IconButton
                size="small"
                onClick={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downloadRallyMp4({
                    packageId,
                    rallyIndex: r.rallyIndex,
                    startMs: r.segmentStartMs,
                    endMs: r.segmentEndMs,
                  }).catch((err) => {
// console.error(err);
                    alert(String(err?.message ?? err));
                  });
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open ChatGPT">
              <IconButton
                size="small"
                // component="a"
                onClick={() =>
                  window.open(
                    buildChatGptUrl(),
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                href={buildChatGptUrl()}
                target="_blank"
                rel="noreferrer"
                sx={{
                  width: 24,
                  height: 24,
                  p: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.65,
                  "&:hover": { opacity: 1 },
                }}
              >
                <img
                  // component="img"
                  src="/chatgpt.ico"
                  alt="ChatGPT"
                  style={{
                    width: 18,
                    height: 18,
                    display: "block",
                    borderRadius: "4px", // optional: soften edges
                    objectFit: "contain",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {buildRallySummaryChips(r).map((label) => (
              <Chip
                key={label}
                size="small"
                variant="outlined"
                label={label}
                sx={{ height: 22, fontSize: 12 }}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => void onExport(r)}
            sx={{ textTransform: "none" }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* main row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.25,
          alignItems: "stretch",
        }}
      >
        {/* left: compact video */}
        <Box
          sx={{
            flex: { xs: "0 0 auto", md: 3 },
            minWidth: 0,
            maxWidth: { xs: "100%", md: "46%" },
          }}
        >
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.12)",
              bgcolor: "rgba(0,0,0,0.04)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                width: "100%",
                maxHeight: compactVideoHeight,
              }}
            >
              <video
                ref={videoRef}
                src={r.videoUrl || ""}
                muted
                loop
                playsInline
                controls
                autoPlay
                style={{
                  width: "100%",
                  height: "auto",
                  //maxHeight: compactVideoHeight,
                  display: "block",
                }}
              />

              <ShotNumberOverlayLayer
                focusedFilter={null}
                videoRef={videoRef}
                overlays={r.shotNumberOverlays}
                segmentStartMs={r.segmentStartMs}
                highlightedShotNumber={undefined}
                highlightedExtraText={undefined}
                rallyIndex={r.rallyIndex}
                firefightsAll={firefightClips}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 1 }}>
            <CoachControls videoRef={videoRef} />
          </Box>
        </Box>

        {/* right: AI */}
        <Box sx={{ flex: { xs: "0 0 auto", md: 5 }, minWidth: 0 }}>
          {/* helper chips */}
          {/* <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            {helperQuestions.map((q) => (
              <Chip key={q} label={q} size="small" onClick={() => setQuestion(q)} sx={{ height: 24 }} />
            ))}
          </Box> */}

          {/* ask row */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              value={question}
              onChange={(e: any) => setQuestion(e.target.value)}
              placeholder="Ask AI..."
              sx={{ flex: 1, minWidth: 420 }}
            />

            {/* <TextField
              size="small"
              value={locator}
              onChange={(e) => setLocator(e.target.value)}
              placeholder="locator (optional)"
              sx={{ width: 200 }}
            /> */}

            <TextField
              select
              size="small"
              value={locator}
              onChange={(e: any) => setLocator(e.target.value)}
              sx={{ width: 200 }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>locator (optional)</em>
              </MenuItem>
              <MenuItem value="near_left">near left</MenuItem>
              <MenuItem value="near_right">near right</MenuItem>
              <MenuItem value="far_left">far left</MenuItem>
              <MenuItem value="far_right">far right</MenuItem>
            </TextField>
            <Button
              size="small"
              variant="contained"
              onClick={() => void onAskAi(question, locator || undefined)}
              sx={{ textTransform: "none" }}
              disabled={!question.trim()}
            >
              Ask AI
            </Button>
          </Box>

          {/* turns */}
          <Box sx={{ mt: 1 }}>
            {turns.length === 0 ? (
              <Box sx={{ fontSize: 12, opacity: 0.65 }}>
                No AI turns yet for this clip.
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {turns
                  .slice()
                  .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
                  .map((t) => (
                    <Box
                      key={t.id}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid rgba(0,0,0,0.12)",
                        p: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ fontWeight: 900, fontSize: 12 }}>
                          {"Question: " + t.question || "AI turn"}
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: "none" }}
                          onClick={() => onDeleteTurn(t.id)}
                        >
                          Delete
                        </Button>
                      </Box>

                      {t.locator ? (
                        <Box sx={{ fontSize: 12, opacity: 0.7, mt: 0.25 }}>
                          locator: {formatLocator(t.locator)}
                        </Box>
                      ) : null}

                      <Box
                        sx={{ mt: 0.75, fontSize: 13, whiteSpace: "pre-wrap" }}
                      >
                        {/* {t.error
                          ? String(t.error)
                          : t.result
                          ? typeof t.result === "string"
                            ? t.result
                            : JSON.stringify(t.result, null, 2)
                          : "(no result)"}{" "} */}

                        {(() => {
                          if (t.error) {
                            return (
                              <Box
                                sx={{
                                  mt: 0.75,
                                  fontSize: 13,
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {String(t.error)}
                              </Box>
                            );
                          }

                          const parsed = tryParseJsonish(t.result);
                          // console.error({ parsed });

                          const extracted = parsed
                            ? extractRallyOverviewFields(parsed)
                            : ({
                                answerToQuestion: undefined,
                                keyTurningPoint: undefined,
                                overallConfidence: undefined,
                              } as const);

                          const {
                            answerToQuestion,
                            keyTurningPoint,
                            overallConfidence,
                          } = extracted;

                          const rawPretty = parsed
                            ? JSON.stringify(parsed, null, 2)
                            : t.result
                              ? typeof t.result === "string"
                                ? t.result
                                : JSON.stringify(t.result, null, 2)
                              : "(no result)";

                          return (
                            <Box
                              sx={{
                                mt: 0.75,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.75,
                              }}
                            >
                              {/* 1) answerToQuestion first */}
                              {answerToQuestion ? (
                                <Box>
                                  <Box
                                    sx={{
                                      display: "inline-block",
                                      fontSize: 13.5,
                                      fontWeight: 800,
                                    }}
                                  >
                                    {"Answer:"}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "inline-block",
                                      fontSize: 13.5,
                                    }}
                                  >
                                    {answerToQuestion}
                                  </Box>
                                </Box>
                              ) : null}

                              {/* 2) keyTurningPoint second */}
                              {keyTurningPoint ? (
                                <Box sx={{ fontSize: 13.5, opacity: 0.85 }}>
                                  <Box
                                    // component="span"
                                    sx={{ fontWeight: 900 }}
                                  >
                                    Turning point:
                                  </Box>
                                  {keyTurningPoint}
                                </Box>
                              ) : null}

                              {/* (optional) confidence line */}
                              {typeof overallConfidence === "number" ? (
                                <Box sx={{ fontSize: 12, opacity: 0.7 }}>
                                  confidence: {overallConfidence.toFixed(2)}
                                </Box>
                              ) : null}

                              {/* 3) full result underneath */}

                              {/* More section (other rallyOverview fields) */}
                              {(() => {
                                const ro = (extracted as any).rallyOverview;

                                if (!ro) return null;

                                const eventSequence: string[] = Array.isArray(
                                  ro.eventSequence,
                                )
                                  ? ro.eventSequence.filter(
                                      (x: any) => typeof x === "string",
                                    )
                                  : [];

                                const uncertaintyNotes: string[] =
                                  Array.isArray(ro.uncertaintyNotes)
                                    ? ro.uncertaintyNotes.filter(
                                        (x: any) => typeof x === "string",
                                      )
                                    : [];

                                const shotInsightsFromPbv: string[] =
                                  Array.isArray(ro.shotInsightsFromPbv)
                                    ? ro.shotInsightsFromPbv.filter(
                                        (x: any) => typeof x === "string",
                                      )
                                    : [];

                                return (
                                  <Box sx={{ mt: 0.25 }}>
                                    <details>
                                      <summary
                                        style={{
                                          cursor: "pointer",
                                          fontSize: 12,
                                          fontWeight: 800,
                                        }}
                                      >
                                        More
                                      </summary>

                                      <Box
                                        sx={{
                                          mt: 0.75,
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.75,
                                        }}
                                      >
                                        {typeof ro.headline === "string" &&
                                        ro.headline ? (
                                          <Box>
                                            <Box
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: 900,
                                              }}
                                            >
                                              Headline:
                                            </Box>
                                            <Box
                                              sx={{
                                                fontSize: 13,
                                                whiteSpace: "pre-wrap",
                                              }}
                                            >
                                              {ro.headline}
                                            </Box>
                                          </Box>
                                        ) : null}

                                        {eventSequence.length ? (
                                          <Box>
                                            <Box
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: 900,
                                              }}
                                            >
                                              Event sequence:
                                            </Box>
                                            <Box
                                              component="ul"
                                              sx={{ m: 0, pl: 2 }}
                                            >
                                              {eventSequence.map((x, i) => (
                                                <li
                                                  key={i}
                                                  style={{
                                                    whiteSpace: "pre-wrap",
                                                    fontSize: 13,
                                                  }}
                                                >
                                                  {x}
                                                </li>
                                              ))}
                                            </Box>
                                          </Box>
                                        ) : null}

                                        {typeof ro.visibleOutcomeNote ===
                                          "string" && ro.visibleOutcomeNote ? (
                                          <Box>
                                            <Box
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: 900,
                                              }}
                                            >
                                              Visible outcome:
                                            </Box>
                                            <Box
                                              sx={{
                                                fontSize: 13,
                                                whiteSpace: "pre-wrap",
                                              }}
                                            >
                                              {ro.visibleOutcomeNote}
                                            </Box>
                                          </Box>
                                        ) : null}

                                        {typeof ro.tacticalPattern ===
                                          "string" && ro.tacticalPattern ? (
                                          <Box>
                                            <Box
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: 900,
                                              }}
                                            >
                                              Tactical pattern:
                                            </Box>
                                            <Box
                                              sx={{
                                                fontSize: 13,
                                                whiteSpace: "pre-wrap",
                                              }}
                                            >
                                              {ro.tacticalPattern}
                                            </Box>
                                          </Box>
                                        ) : null}

                                        {uncertaintyNotes.length ? (
                                          <Box>
                                            <Box
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: 900,
                                              }}
                                            >
                                              Uncertainty notes:
                                            </Box>
                                            <Box
                                              component="ul"
                                              sx={{ m: 0, pl: 2 }}
                                            >
                                              {uncertaintyNotes.map((x, i) => (
                                                <li
                                                  key={i}
                                                  style={{
                                                    whiteSpace: "pre-wrap",
                                                    fontSize: 13,
                                                  }}
                                                >
                                                  {x}
                                                </li>
                                              ))}
                                            </Box>
                                          </Box>
                                        ) : null}

                                        {shotInsightsFromPbv.length ? (
                                          <Box>
                                            <Box
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: 900,
                                              }}
                                            >
                                              PBV shot hints:
                                            </Box>
                                            <Box
                                              component="ul"
                                              sx={{ m: 0, pl: 2 }}
                                            >
                                              {shotInsightsFromPbv.map(
                                                (x, i) => (
                                                  <li
                                                    key={i}
                                                    style={{
                                                      whiteSpace: "pre-wrap",
                                                      fontSize: 13,
                                                    }}
                                                  >
                                                    {x}
                                                  </li>
                                                ),
                                              )}
                                            </Box>
                                          </Box>
                                        ) : null}
                                      </Box>
                                    </details>
                                  </Box>
                                );
                              })()}

                              {/* 3) full result underneath */}
                              {/* <Box
  sx={{
    fontSize: 12.5,
    whiteSpace: "pre-wrap",
    borderRadius: 1.5,
    border: "1px solid rgba(0,0,0,0.10)",
    bgcolor: "rgba(0,0,0,0.02)",
    p: 0.75,
    overflowX: "auto",
  }}
>
  {rawPretty}
</Box> */}

                              {/* <Box
                                sx={{
                                  fontSize: 12.5,
                                  whiteSpace: "pre-wrap",
                                  borderRadius: 1.5,
                                  border: "1px solid rgba(0,0,0,0.10)",
                                  bgcolor: "rgba(0,0,0,0.02)",
                                  p: 0.75,
                                  overflowX: "auto",
                                }}
                              >
                                {rawPretty}
                              </Box> */}
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function parseTokens(label: string | null | undefined): string[] {
  if (!label) return [];
  return label
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function lowerMap(tokens: string[]): Record<string, true> {
  const m: Record<string, true> = {};
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (!t) continue;
    m[String(t).toLowerCase()] = true;
  }
  return m;
}

function findFirstMatch(tokensLower: string[], re: RegExp): string | null {
  for (let i = 0; i < tokensLower.length; i++) {
    const t = tokensLower[i];
    const m = t.match(re);
    if (m && m[1]) return m[1];
  }
  return null;
}

function hasAnyPrefix(tokensLower: string[], prefixes: string[]): boolean {
  for (let i = 0; i < prefixes.length; i++) {
    const p = prefixes[i]!;
    for (let j = 0; j < tokensLower.length; j++) {
      const t = tokensLower[j]!;
      if (t === p || t.indexOf(p) === 0) return true;
    }
  }
  return false;
}

function formatOverlayLabelSmart(active: {
  shotNumber: number;
  label?: string | null;
}): string | null {
  const shotIdx0 = Math.max(0, (active.shotNumber ?? 1) - 1);
  const isServe = shotIdx0 === 0;
  const isReturn = shotIdx0 === 1;

  const rawTokens = parseTokens(active.label);
  if (!rawTokens.length) return null;

  const lowerTokens: string[] = [];
  for (let i = 0; i < rawTokens.length; i++) {
    lowerTokens.push(rawTokens[i]!.toLowerCase());
  }

  const endZone = findFirstMatch(lowerTokens, /^end_zone:(.+)$/);

  const isFault =
    hasAnyPrefix(lowerTokens, ["fault_", "forced_fault"]) ||
    lowerMap(rawTokens)["forced_fault"] === true;

  const zoneWord =
    endZone === "deep" ? "deep" : endZone === "short" ? "short" : null;

  const dropPatterns: RegExp[] = [
    /^end_zone:(net|out|short)$/i,
    /^fault_(net|out|short)\b/i,
  ];

  const seen: Record<string, true> = {};
  const kept: string[] = [];

  const keep = (t: string) => {
    const n = t.toLowerCase().trim();
    if (!n) return;
    if (seen[n]) return;
    seen[n] = true;
    kept.push(t);
  };

  // 1) Serve/return phrasing rules
  if (isServe) {
    const serveMatters = isFault || zoneWord === "deep" || zoneWord === "short";
    if (serveMatters) {
      if (zoneWord) keep(zoneWord + " serve");
      else keep("serve");
    }
  }

  if (isReturn) {
    const returnMatters = zoneWord === "deep" || zoneWord === "short";
    if (returnMatters && zoneWord) keep(zoneWord + " return");
  }

  // 2) Normalize and filter base tokens
  const normalized: string[] = [];

  for (let i = 0; i < rawTokens.length; i++) {
    let t = rawTokens[i]!;
    const tl = t.toLowerCase().trim();

    if (tl === "serve" || tl === "return") continue;

    // drop obvious tokens
    let drop = false;
    for (let k = 0; k < dropPatterns.length; k++) {
      if (dropPatterns[k]!.test(tl)) {
        drop = true;
        break;
      }
    }
    if (drop) continue;

    // if we already encoded deep/short into "deep serve/return", drop end_zone:deep/short
    if (
      (isServe || isReturn) &&
      (tl === "end_zone:deep" || tl === "end_zone:short")
    )
      continue;

    // normalize end_* -> *
    if (tl.indexOf("end_") === 0 && tl.length > 4) {
      t = t.slice(4);
    }

    normalized.push(t);
  }

  // Build present from normalized (needed for key:value dropping)
  let present: Record<string, true> = {};
  for (let i = 0; i < normalized.length; i++) {
    present[normalized[i]!.toLowerCase()] = true;
  }

  // Drop "key:value" if "value" exists as standalone token
  const normalized2: string[] = [];
  for (let i = 0; i < normalized.length; i++) {
    const t = normalized[i]!;
    const tl = t.toLowerCase().trim();

    const m = tl.match(/^([a-z0-9_]+):(.+)$/);
    if (m && m[2]) {
      const value = m[2].trim();
      if (present[value]) continue;
    }

    normalized2.push(t);
  }

  // Canonicalize "_" vs ":" duplicates (prefer ":" form)
  const bestByCanon: Record<string, { t: string; score: number }> = {};
  for (let i = 0; i < normalized2.length; i++) {
    const t = normalized2[i]!;
    const tl = t.toLowerCase().trim();

    const canon = tl.replace(/_/g, ":");
    const score = tl.indexOf(":") >= 0 ? 2 : 1;

    const prev = bestByCanon[canon];
    if (!prev || score > prev.score) bestByCanon[canon] = { t, score };
  }

  const normalized3 = Object.keys(bestByCanon)
    .sort()
    .map((k) => bestByCanon[k]!.t);

  // IMPORTANT: rebuild present from normalized3 BEFORE removing zone:* redundancy
  present = {};
  for (let i = 0; i < normalized3.length; i++) {
    present[normalized3[i]!.toLowerCase()] = true;
  }

  // detect whether we already emitted short/deep serve/return
  const hasShortReturn =
    present["short return"] === true || seen["short return"] === true;
  const hasDeepReturn =
    present["deep return"] === true || seen["deep return"] === true;
  const hasShortServe =
    present["short serve"] === true || seen["short serve"] === true;
  const hasDeepServe =
    present["deep serve"] === true || seen["deep serve"] === true;

  for (let i = 0; i < normalized3.length; i++) {
    const t = normalized3[i]!;
    const tl = t.toLowerCase().trim();

    // Drop standalone short/deep if already encoded in "short return"/"deep serve" etc.
    if (tl === "short" && (hasShortReturn || hasShortServe)) continue;
    if (tl === "deep" && (hasDeepReturn || hasDeepServe)) continue;

    // Drop zone:* (or end_zone:*) if we already have the bare value token
    const m = tl.match(/^(end_zone|zone):(.+)$/);
    if (m && m[2]) {
      const zone = m[2].trim();
      if (present[zone]) continue; // e.g. have "kitchen" so skip "zone:kitchen"
    }

    keep(t);
  }

  // If you truly want uncapped, delete these two lines:
  const MAX_TOKENS = 4;
  const display = kept.slice(0, MAX_TOKENS);

  return display.length ? display.join(", ") : null;
}

function useRect(ref: React.RefObject<HTMLElement | null>) {
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => setRect(el.getBoundingClientRect());

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    // also update on scroll (rect changes)
    window.addEventListener("scroll", update, true);

    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update, true);
    };
  }, [ref]);

  return rect;
}

function useVideoIntrinsic(videoRef: React.RefObject<HTMLVideoElement | null>) {
  const [intrinsic, setIntrinsic] = React.useState<{
    w: number;
    h: number;
  } | null>(null);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const update = () => {
      const w = v.videoWidth;
      const h = v.videoHeight;
      if (w > 0 && h > 0) setIntrinsic({ w, h });
    };

    update();
    v.addEventListener("loadedmetadata", update);
    return () => v.removeEventListener("loadedmetadata", update);
  }, [videoRef]);

  return intrinsic;
}

// function sampleFollowUv(
//   followUv: Array<{ dtMs: number; u: number; v: number }>,
//   dtMs: number
// ): { dtMs: number; u: number; v: number } | null {
//   if (!Array.isArray(followUv) || followUv.length === 0) return null;

//   // Ensure sorted by dtMs (backend should already do this, but be safe)
//   // If you know it's sorted, you can remove this copy for perf.
//   const pts = followUv.slice().sort((a, b) => a.dtMs - b.dtMs);

//   // Clamp outside range
//   if (dtMs <= pts[0]!.dtMs) return { dtMs, u: pts[0]!.u, v: pts[0]!.v };
//   const last = pts[pts.length - 1]!;
//   if (dtMs >= last.dtMs) return { dtMs, u: last.u, v: last.v };

//   // Find bracketing points (linear scan; arrays are small, so this is fine)
//   for (let i = 1; i < pts.length; i++) {
//     const a = pts[i - 1]!;
//     const b = pts[i]!;
//     if (dtMs <= b.dtMs) {
//       const denom = b.dtMs - a.dtMs;
//       const t = denom > 0 ? (dtMs - a.dtMs) / denom : 0;

//       return {
//         dtMs,
//         u: a.u + (b.u - a.u) * t,
//         v: a.v + (b.v - a.v) * t,
//       };
//     }
//   }

//   // Shouldn't hit, but return last just in case
//   return { dtMs, u: last.u, v: last.v };
// }

type FollowUvPoint = {
  dtMs: number;
  u: number;
  v: number;
  confidence?: number | null;
};

export function sampleFollowUv(
  points: FollowUvPoint[],
  dtMs: number,
): { u: number; v: number } | null {
  if (!points?.length) return null;

  // assume sorted by dtMs; if not, you can sort once on the backend
  if (dtMs <= points[0]!.dtMs) return { u: points[0]!.u, v: points[0]!.v };
  const last = points[points.length - 1]!;
  if (dtMs >= last.dtMs) return { u: last.u, v: last.v };

  // binary search for segment
  let lo = 0;
  let hi = points.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid]!.dtMs <= dtMs) lo = mid;
    else hi = mid;
  }

  const a = points[lo]!;
  const b = points[hi]!;
  const span = b.dtMs - a.dtMs;
  if (span <= 0) return { u: a.u, v: a.v };

  const t = (dtMs - a.dtMs) / span;
  return {
    u: a.u + (b.u - a.u) * t,
    v: a.v + (b.v - a.v) * t,
  };
}

function computeReactionMsForActiveShot(args: {
  overlays: ShotNumberOverlay[];
  active: ShotNumberOverlay;
}): number | null {
  const { overlays, active } = args;

  const idx = overlays.findIndex((o) => o.shotNumber === active.shotNumber);
  if (idx <= 0) return null;

  const prev = overlays[idx - 1];
  const prevStart = prev?.rawShot?.start_ms;
  const curStart = active?.rawShot?.start_ms;

  if (typeof prevStart !== "number" || typeof curStart !== "number")
    return null;

  const reaction = curStart - prevStart;
  if (!Number.isFinite(reaction) || reaction < 0) return null;
  return reaction;
}

function formatRequiredReactionTime(ms: number): string {
  if (!Number.isFinite(ms)) return "";
  const sec = ms / 1000;
  return `${sec.toFixed(2)}s`;
}

function getMphFromShot(active: ShotNumberOverlay): number | null {
  const s = active?.rawShot as any;
  const mph = s?.resulting_ball_movement?.speed;
  return typeof mph === "number" && Number.isFinite(mph) ? mph : null;
}

export const DEFAULT_MPH_HIGHLIGHT_VALUE_SHOT_NUMBER_OVERLAY = 0;

function teamForPlayerId(pid: number | null): 0 | 1 | null {
  if (typeof pid !== "number") return null;
  // Per your POC2 convention: players 0/1 => team 0; players 2/3 => team 1
  return pid <= 1 ? 0 : 1;
}

/**
 *
 *
 *
 * Returns true if this shot overlay should display the focused badge icon.
 * Uses storyKinds/story + a few derived fields as fallbacks.
 */
export function ShotNumberOverlayLayer(props: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  overlays?: ShotNumberOverlay[];
  segmentStartMs: number;

  // NEW (optional): if you already know “this segment is about shot N”
  highlightedShotNumber: number | undefined;
  highlightedExtraText: string | undefined;
  breakShotNumber?: number | undefined;
  reactionHighlightMs_ignore?: number; // e.g. 600
  //mphHighlightValue?: number; // e.g. 40
  showThresholdValuesInLabel?: boolean; // optional (default true)

  rallyIndex: number;
  firefightsAll: FirefightClip[]; // you can pass clipsAll
  firefightIcon?: string; // default "🔥"
  firefightPlacement?: "corner" | "both" | "left"; // default "corner"
  focusedFilter: FocusedReasonStakeForTeam | null;
  renderPlayerIdentity?: RenderPlayerIdentity;
  insightsToCvPlayerIndex?: PlayerIndexMap;
  isFromFullPopout?: boolean;
  persistentExtraTextForOverlay?: (
    overlay: ShotNumberOverlay,
  ) => string | undefined;
  onActiveShotNumberChange?: (shotNumber: number | null) => void;
  manualHighlightLabel?: string;
}) {
  const {
    videoRef,
    overlays,
    segmentStartMs,
    highlightedShotNumber,
    highlightedExtraText,
    //reactionHighlightMs = 600,

    showThresholdValuesInLabel = false,
    rallyIndex,
    firefightsAll,
    firefightIcon = "🔥",
    firefightPlacement = "both",
    focusedFilter,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    isFromFullPopout = false,
    persistentExtraTextForOverlay,
    onActiveShotNumberChange,
    manualHighlightLabel = "FOCUS",
  } = props;

  const reactionHighlightMs = 500;

  const mphHighlightValue = DEFAULT_MPH_HIGHLIGHT_VALUE_SHOT_NUMBER_OVERLAY; // using 0 to see them all
  const ffIcon = props.firefightIcon ?? "🔥";
  const ffPlacement = props.firefightPlacement ?? "both";

  const [tMsRel, setTMsRel] = React.useState(0);

  const overlaysForDisplay = React.useMemo(() => {
    if (!overlays?.length) return overlays;
    return overlays.map((o) => withPresentationEnvelope(o));
  }, [overlays]);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    let raf = 0;
    const tick = () => {
      const vv = videoRef.current;
      if (vv) {
        const ms = vv.currentTime * 1000;
        if (Number.isFinite(ms)) setTMsRel(Math.round(ms));
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoRef]);

  const videoDurMs = (videoRef.current?.duration ?? 0) * 1000;

  const useAbsoluteClock = React.useMemo(() => {
    if (!overlays?.length) return false;
    const maxHideAt = Math.max(...overlays.map((o) => o.hideAtMsRel));
    return maxHideAt > videoDurMs + 1500;
  }, [overlays, videoDurMs]);

  const tClock = useAbsoluteClock ? segmentStartMs + tMsRel : tMsRel;

  const active = React.useMemo(
    () => pickActiveShotOverlay(overlaysForDisplay, tClock),
    [overlaysForDisplay, tClock],
  );

  const lastEmittedShotNumberRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const nextShotNumber =
      typeof active?.shotNumber === "number" ? active.shotNumber : null;

    if (lastEmittedShotNumberRef.current === nextShotNumber) return;

    lastEmittedShotNumberRef.current = nextShotNumber;
    props.onActiveShotNumberChange?.(nextShotNumber);
  }, [active?.shotNumber, props.onActiveShotNumberChange]);

  // Highlight detection supports either:
  // - a boolean on the overlay
  // - a “currently highlighted shot number” passed in
  // const isHighlighted =
  //   // @ts-expect-error: tolerate a few possible field names without forcing a type refactor right now
  //   Boolean(active.isHighlighted ?? active.highlighted ?? active.emphasize) ||
  //   (typeof highlightedShotNumber === "number" &&
  //     active.shotNumber === highlightedShotNumber);

  // const label = `${active.shotNumber} (${active.playerId})`;

  // const baseLabel = `${active.shotNumber} (${active.playerId})`;

  // const label = isHighlighted
  //   ? highlightedExtraText ? `${baseLabel}${highlightedExtraText ? ` • ${highlightedExtraText}` : ""}`: `${baseLabel}${highlightedExtraText}` :
  //   baseLabel;

  const reactionMs = React.useMemo(() => {
    if (!overlaysForDisplay?.length || !active) return null;
    return computeReactionMsForActiveShot({
      overlays: overlaysForDisplay,
      active,
    });
  }, [overlaysForDisplay, active]);

  const mph = React.useMemo(() => {
    if (!active) return null;
    return getMphFromShot(active);
  }, [active]);

  const firefightRangesThisRally = React.useMemo(() => {
    const all = props.firefightsAll ?? [];
    const ranges = all
      .filter((c) => c.seq?.rallyIndex === props.rallyIndex)
      .map((c) => ({
        start: c.seq.startShotNumber,
        end: c.seq.endShotNumber,
      }))
      // optional: normalize just in case
      .filter((r) => Number.isFinite(r.start) && Number.isFinite(r.end))
      .map((r) => (r.start <= r.end ? r : { start: r.end, end: r.start }))
      // optional: merge overlaps so membership checks are cheap
      .sort((a, b) => a.start - b.start);

    // merge overlaps/adjacent
    const merged: Array<{ start: number; end: number }> = [];
    for (const r of ranges) {
      const last = merged[merged.length - 1];
      if (!last || r.start > last.end + 1) merged.push({ ...r });
      else last.end = Math.max(last.end, r.end);
    }
    return merged;
  }, [props.firefightsAll, props.rallyIndex]);

  if (!active) return null;

  const showFocused =
    !!focusedFilter && shotMatchesFocusedFilter(active, focusedFilter);

  const FocusIcon = showFocused
    ? STORY_FILTER_ICON[focusedFilter!.reason]
    : null;
  const focusColor = showFocused ? teamRgba(focusedFilter!.team, 1) : null;

  const activeFilterLine2 = showFocused
    ? (focusedFilter?.reason ?? "Filtered")
    : undefined;

  //console.error({focusedFilter, active, showFocused, focusColor});

  const opacity = overlayOpacityMsRel(tClock, active);
  if (opacity <= 0) return null;

  const isReactionHighlighted =
    typeof reactionMs === "number" && reactionMs <= reactionHighlightMs;

  const isMphHighlighted = typeof mph === "number" && mph >= mphHighlightValue;

  const isAutoHighlighted = isReactionHighlighted || isMphHighlighted;

  //     const label = isHighlighted
  // ? `${baseLabel}${highlightedExtraText ? ` • ${highlightedExtraText}` : ""}`
  // : baseLabel;

  // const autoExtraParts: string[] = [];
  // if (typeof reactionMs === "number" && isReactionHighlighted) {
  //   autoExtraParts.push(`${Math.round(reactionMs)}ms`);
  // }
  // if (typeof mph === "number" && isMphHighlighted) {
  //   autoExtraParts.push(`${mph.toFixed(0)}mph`);
  // }

  // const isManualHighlighted =
  //   // @ts-expect-error tolerate possible field names
  //   Boolean(active.isHighlighted ?? active.highlighted ?? active.emphasize) ||
  //   (typeof highlightedShotNumber === "number" &&
  //     active.shotNumber === highlightedShotNumber);

  // const isHighlighted = isManualHighlighted || isAutoHighlighted;

  
    const manualExtraText = (props.highlightedExtraText ?? "").trim();

  const autoExtraParts: string[] = [];
  if (typeof reactionMs === "number") {
    autoExtraParts.push(
      `required pre-shot reaction ${formatRequiredReactionTime(reactionMs)}`,
    );
  }
  if (typeof mph === "number" && isMphHighlighted) {
    autoExtraParts.push(`${mph.toFixed(0)}mph`);
  }

  const isManualHighlighted =
    // @ts-expect-error tolerate possible field names
    Boolean(active.isHighlighted ?? active.highlighted ?? active.emphasize) ||
    (typeof highlightedShotNumber === "number" &&
      active.shotNumber === highlightedShotNumber);

  // Turning-point highlighting needs to be context-specific; do not add it
  // automatically just because the rally has a break shot.
  const isBreakHighlighted = false;
  // const isBreakHighlighted =
  //   typeof props.breakShotNumber === "number" &&
  //   active.shotNumber === props.breakShotNumber;

  const isHighlighted =
    isManualHighlighted || isBreakHighlighted || isAutoHighlighted;

  const combinedExtraParts = (() => {
    const parts: string[] = [];

    const persistent = persistentExtraTextForOverlay?.(active)?.trim();
    if (persistent) parts.push(persistent);

    if (isManualHighlighted && manualExtraText) {
      parts.push(manualExtraText);
    }

    // if (isBreakHighlighted) {
    //   parts.push("Turning Point");
    // }

    if (autoExtraParts.length) {
      parts.push(...autoExtraParts);
    }

    return Array.from(new Set(parts.filter(Boolean)));
  })();

  const labelExtraText = combinedExtraParts.length
    ? `• ${combinedExtraParts.join(" • ")}`
    : null;

  // const extraText = isManualHighlighted
  //   ? (props.highlightedExtraText ?? "")
  //   : autoExtraParts.join(" • ");

  const highlightStyle = isManualHighlighted
    ? {
        bgcolor: "rgba(255,230,140,0.96)",
        color: "rgba(0,0,0,0.96)",
        border: "2px solid rgba(0,0,0,0.55)",
        boxShadow:
          "0 0 0 3px rgba(255,215,64,0.30), 0 10px 22px rgba(0,0,0,0.32)",
        fontSize: 16,
        px: 1.35,
        py: 0.5,
          letterSpacing: 0.1,
        }
    : isBreakHighlighted
      ? {
          bgcolor: "rgba(255,138,64,0.96)",
          color: "rgba(0,0,0,0.96)",
          border: "2px solid rgba(255,255,255,0.58)",
          boxShadow:
            "0 0 0 3px rgba(255,118,35,0.30), 0 10px 22px rgba(0,0,0,0.32)",
          fontSize: 15,
          px: 1.25,
          py: 0.45,
          letterSpacing: 0.1,
        }
    : isAutoHighlighted
      ? {
          bgcolor: "rgba(255,255,255,0.90)",
          color: "rgba(0,0,0,0.92)",
          border: "1px solid rgba(0,0,0,0.28)",
          boxShadow:
            "0 0 0 2px rgba(255,255,255,0.25), 0 8px 18px rgba(0,0,0,0.35)",
          fontSize: 14,
          px: 1.15,
          py: 0.38,
          letterSpacing: 0.15,
        }
      : null;

  // const baseLabel = `Shot ${active.shotNumber} • p${active.playerId}`;
  // const label =
  //   isHighlighted && extraText ? `${baseLabel} • ${extraText}` : baseLabel;

  const shotLabelPrefix = `Shot ${active.shotNumber} •`;
  const shotLabelPlayerSize = isFromFullPopout ? 32 : 24;
  const shotLabelPlayerId =
    typeof active.playerId === "number" && insightsToCvPlayerIndex
      ? toDisplayPlayerId(active.playerId, insightsToCvPlayerIndex)
      : active.playerId;
  const shotLabelPlayerNode =
    renderPlayerIdentity?.({
      playerId: shotLabelPlayerId,
      avatarOnly: true,
      size: shotLabelPlayerSize,
      borderRadius: 7,
      borderWidth: "2px",
    }) ??
    renderPlayerIdentity?.({
      playerId: shotLabelPlayerId,
      noLabel: true,
      size: shotLabelPlayerSize,
      borderRadius: 7,
      fontSize: isFromFullPopout ? 12 : 10,
      fontColor: "white",
      borderWidth: "2px",
    }) ?? (
      <Box
        component="span"
        sx={{
          width: shotLabelPlayerSize,
          height: shotLabelPlayerSize,
          borderRadius: "7px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: teamRgba(Number(shotLabelPlayerId) >= 2 ? 1 : 0, 0.86),
          color: "rgba(255,255,255,0.98)",
          border: "2px solid rgba(255,255,255,0.72)",
          fontSize: isFromFullPopout ? 12 : 10,
          fontWeight: 950,
          lineHeight: 1,
          boxSizing: "border-box",
          flex: "0 0 auto",
        }}
      >
        p{shotLabelPlayerId ?? "?"}
      </Box>
    );

  const persistentExtraText = persistentExtraTextForOverlay?.(active)?.trim();
  const manualHighlightLabelText = manualHighlightLabel.trim();

  // const combinedExtraParts = [
  //   persistentExtraText ? persistentExtraText : null,
  //   isHighlighted && extraText ? extraText : null,
  // ].filter(Boolean) as string[];


// const combinedExtraParts = (() => {
//   if (isManualHighlighted) {
//     const primary = persistentExtraText || extraText;
//     return primary ? [primary] : [];
//   }

//   if (
//     persistentExtraText &&
//     extraText &&
//     persistentExtraText === extraText
//   ) {
//     return [persistentExtraText];
//   }

//   return [
//     persistentExtraText || null,
//     isHighlighted && extraText ? extraText : null,
//   ].filter(Boolean);
// })();

  // const labelExtraText = combinedExtraParts.length
  //   ? `• ${combinedExtraParts.join(" • ")}`
  //   : null;
  // // const labelExtraText = isHighlighted && extraText ? `• ${extraText}` : null;



  const isInAnyFirefight =
    firefightRangesThisRally.length > 0 &&
    typeof active.shotNumber === "number" &&
    firefightRangesThisRally.some(
      (r) => active.shotNumber >= r.start && active.shotNumber <= r.end,
    );

  const isFirefightHighlighted = isInAnyFirefight && isHighlighted;
  const firefightFlair = isFirefightHighlighted
    ? {
        // warm, but subtle
        boxShadow:
          "0 0 0 1px rgba(255,170,60,0.22), 0 10px 18px rgba(0,0,0,0.28)",
        border: "1px solid rgba(255,170,60,0.28)",
        // little “ember underline”
        "&:after": {
          content: '""',
          position: "absolute",
          left: 10,
          right: 10,
          bottom: -3,
          height: 3,
          borderRadius: 999,
          background:
            "linear-gradient(90deg, rgba(255,170,60,0) 0%, rgba(255,170,60,0.55) 35%, rgba(255,110,20,0.55) 65%, rgba(255,170,60,0) 100%)",
          opacity: 0.55,
          filter: "blur(0.2px)",
          pointerEvents: "none",
        },
      }
    : null;

  const shimmer = isFirefightHighlighted
    ? {
        animation: "ffPulse 900ms ease-in-out infinite",
        "@keyframes ffPulse": {
          "0%": { transform: "translate(-50%, -50%) scale(1)" },
          "50%": { transform: "translate(-50%, -50%) scale(1.01)" },
          "100%": { transform: "translate(-50%, -50%) scale(1)" },
        },
      }
    : null;

  const showLeftFf =
    isInAnyFirefight && (ffPlacement === "both" || ffPlacement === "left");
  const showCornerFf =
    isInAnyFirefight && (ffPlacement === "both" || ffPlacement === "corner");

  return (
    <Box
      sx={{
        pointerEvents: "none",
        position: "absolute",
        inset: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 42,
          transform: "translate(-50%, -50%)",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          opacity,
          userSelect: "none",

          // Base pill
          bgcolor: "rgba(0,0,0,0.72)",
          color: "#fff",
          px: 0.9,
          py: 0.3,
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 900,
          lineHeight: 1,
          boxShadow: 2,
          border: "1px solid rgba(255,255,255,0.35)",

          // Highlighted styling (the “instant recognition” part)
          ...(isHighlighted ? highlightStyle : {}),
          ...(firefightFlair ?? {}),
          ...(shimmer ?? {}),

          // ...(isHighlighted
          //   ? {
          //       bgcolor: "rgba(255,215,64,0.92)", // warm highlight
          //       color: "rgba(0,0,0,0.92)",
          //       border: "1px solid rgba(0,0,0,0.45)",
          //       boxShadow: "0 0 0 2px rgba(255,215,64,0.35), 0 8px 18px rgba(0,0,0,0.35)",
          //       fontSize: 14,
          //       px: 1.15,
          //       py: 0.38,
          //       letterSpacing: 0.2,
          //     }
          //   : null),
        }}
      >
        {/* {isHighlighted ? `${label}` : label} */}

        {isManualHighlighted && manualHighlightLabelText ? (
          <Box
            sx={{
              mr: 0.8,
              px: 0.7,
              py: 0.15,
              borderRadius: "999px",
              bgcolor: "rgba(0,0,0,0.10)",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: 0.4,
            }}
          >
            {manualHighlightLabelText}
          </Box>
        ) : null}
        {showLeftFf ? (
          // <Box
          //   component="span"
          //   sx={{
          //     display: "inline-flex",
          //     alignItems: "center",
          //     justifyContent: "center",
          //     mr: 0.6,
          //     fontSize: 12,
          //     lineHeight: 1,
          //     opacity: 0.92,
          //     transform: "translateY(-0.5px)",
          //   }}
          // >
          <>{ffIcon}</>
        ) : // </Box>
        null}

        {FocusIcon ? (
          // <Box
          //   component="span"
          //   sx={{
          //     display: "inline-flex",
          //     alignItems: "center",
          //     justifyContent: "center",
          //     mr: 0.45,
          //     transform: "translateY(1px)",
          //     opacity: 0.95,
          //   }}
          // >
          // <FocusIcon sx={{ fontSize: 15, color: focusColor ?? "white" }} />
          <Box
            sx={{
              fontSize: 15,
              color: focusColor ?? "white",
              display: "inline-flex",
            }}
          >
            {React.createElement(FocusIcon as React.ElementType)}
          </Box>
        ) : // </Box>
        null}

        {activeFilterLine2 ? (
          <Box
            sx={{
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                display: "inline-block",
              }}
            >
              {shotLabelPrefix}
            </Box>

            {shotLabelPlayerNode}

            {labelExtraText ? (
              <Box
                sx={{
                  display: "inline-block",
                }}
              >
                {labelExtraText}
              </Box>
            ) : null}
          </Box>
        ) : (
          // <Box
          //   sx={{
          //     display: "inline-flex",
          //     flexDirection: "column",
          //     alignItems: "center",
          //     justifyContent: "center",
          //     gap: "2px",
          //     transform: "translateY(0.5px)", // tiny optical centering; remove if you dislike
          //   }}
          // >
          //   <Box sx={{ lineHeight: 1 }}>{label}</Box>

          //   <Box
          //     sx={{
          //       lineHeight: 1,
          //       display: "inline-flex",
          //       alignItems: "center",
          //       gap: 0.6,
          //       whiteSpace: "nowrap",
          //     }}
          //   >
          //     <Box
          //       sx={{
          //         display: "inline-block",
          //       }}
          //     >
          //       {shotLabelPrefix}
          //     </Box>

          //     {renderPlayerIdentity?.({
          //       playerId: active.playerId,
          //       size: 18,
          //       borderRadius: 5,
          //       fontSize: 10,
          //     }) ?? `P${active.playerId}`}

          //     {labelExtraText ? (
          //       <Box
          //         sx={{
          //           display: "inline-block",
          //         }}
          //       >
          //         {labelExtraText}
          //       </Box>
          //     ) : null}
          //   </Box>

          //   <Box
          //     sx={{
          //       fontWeight: 800,
          //       lineHeight: 1,
          //       opacity: 0.9,
          //       backgroundColor: teamRgba(focusedFilter!.team, 0.35),
          //       borderRadius: "4px",
          //       width: "100%",
          //       textAlign: "center",
          //     }}
          //   >
          //     {activeFilterLine2}
          //   </Box>
          // </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                display: "inline-block",
              }}
            >
              {shotLabelPrefix}
            </Box>

            {shotLabelPlayerNode}

            {labelExtraText ? (
              <Box
                sx={{
                  display: "inline-block",
                }}
              >
                {labelExtraText}
              </Box>
            ) : null}
          </Box>
          // <>{label}</>
        )}

        {showCornerFf ? (
          // <Box
          //   component="span"
          //   sx={{
          //     position: "absolute",
          //     right: -2,
          //     top: -6,
          //     fontSize: 11,
          //     lineHeight: 1,
          //     opacity: 0.9,
          //     pointerEvents: "none",
          //     // bgcolor: "rgba(0,0,0,0.45)",
          //     color: "white",
          //     border: "1px solid rgba(255,255,255,0.20)",
          //     borderRadius: 999,
          //     px: 0.45,
          //     py: 0.05,
          //   }}
          // >
          <>{ffIcon}</>
        ) : // </Box>
        null}
      </Box>
      {/* 
      {showLeftFf ? (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 0.6,
            fontSize: 12,
            lineHeight: 1,
            opacity: 0.92,
            transform: "translateY(-0.5px)",
          }}
        >
          {ffIcon}
        </Box>
      ) : null}

      {label}

      {showCornerFf ? (
        <Box
          component="span"
          sx={{
            position: "absolute",
            right: -2,
            top: -6,
            fontSize: 11,
            lineHeight: 1,
            opacity: 0.9,
            pointerEvents: "none",
            bgcolor: "rgba(0,0,0,0.45)",
            color: "white",
            border: "1px solid rgba(255,255,255,0.20)",
            borderRadius: 999,
            px: 0.45,
            py: 0.05,
          }}
        >
          {ffIcon}
        </Box>
      ) : null} */}
    </Box>
  );
}

function withPresentationEnvelope(
  o: ShotNumberOverlay,
  opts?: { preMs?: number; holdMs?: number; fadeMs?: number },
): ShotNumberOverlay {
  const preMs = opts?.preMs ?? 50; // show a bit BEFORE point
  const holdMs = opts?.holdMs ?? 1900; // hold after point
  const fadeMs = opts?.fadeMs ?? 700; // fade duration

  const showFromMsRel = o.atMsRel - preMs;
  const fullFromMsRel = o.atMsRel - Math.min(40, preMs); // quick ramp
  const fadeStartMsRel = o.atMsRel + holdMs;
  const hideAtMsRel = fadeStartMsRel + fadeMs;

  return {
    ...o,
    showFromMsRel,
    fullFromMsRel,
    fadeStartMsRel,
    hideAtMsRel,
  };
}
